import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import {
  CrossCircledIcon,
  LockClosedIcon,
  ReloadIcon,
} from "@radix-ui/react-icons";
import React, { useMemo, useRef, useState, useEffect } from "react";
import "./Checkout.css";
import { useParams } from "@/lib/browserRouter";
import {
  detectCardType,
  formatCreditCardNumber,
  formatExpDate,
  formatName,
  formatPhoneNumber,
  formatTaxId,
  getInstallments,
  getPeriod,
  isValidBrazilianCompanyName,
  isValidBrazilianPhone,
  isValidCNPJ,
  isValidCPF,
  isValidName,
  isValidCreditCardNumber,
  isValidEmail,
  getAffiliate,
} from "./helpers";
import { useQuery } from "@/hooks/useQuery";
import InactivePlan from "./InactivePlan";
import copyToClipboard from "@/utils/copyToClipboard";
import { CircleSlashIcon, CopyIcon } from "lucide-react";
import PixTimer from "./PixTimer";
import { sendPixelEvent } from "./functions/sendPixelEvent";
import { getUniqueId } from "@/utils/getUniqueId";
import { getCookie } from "@/utils/getCookie";
import { setCookie } from "@/utils/setCookie";
import InactiveProduct from "./InactiveProduct";
import { usePurchaseStatus } from "@/hooks/usePurchaseStatus";
import StripePaymentForm from "./StripePaymentForm";
import { Skeleton } from "@/shadcn/components/ui/skeleton";
import ErrorPage from "../ErrorPage/ErrorPage";
import { logException, logMessage } from "@/utils/sentryLogger";
import { isTransientNetworkError } from "@/utils/networkErrors";
import { ComponentRenderer } from "@/components/CheckoutRenderer";
import { cn } from "@/lib/utils";
import Loading from "@/components/Loading";
import { loadCheckoutIntegrations } from "@/utils/checkoutIntegrations";
import { mediaUrl } from "@/utils/mediaUrl";

// Hook to detect mobile device
const useIsMobile = () => {
  // Initialize with actual window width if available
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 768;
    }
    return false;
  });

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount (in case SSR or initial state was wrong)
    checkIsMobile();

    // Add event listener
    window.addEventListener("resize", checkIsMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  return isMobile;
};

/** CF/LB bases: local `vite` uses VITE_DEV_* (staging); production build uses VITE_STABLE_* unless VITE_USE_STAGING_API=true. */
function pickApiBase(devKey, stagingKey, stableKey) {
  const dev = import.meta.env[devKey];
  const staging = import.meta.env[stagingKey];
  const stable = import.meta.env[stableKey];
  const forceStaging =
    import.meta.env.VITE_USE_STAGING_API === "true" ||
    import.meta.env.VITE_USE_STAGING_API === true;

  if (import.meta.env.DEV && dev) {
    return dev;
  }
  if (!import.meta.env.DEV && forceStaging) {
    return staging || dev || stable;
  }
  return stable || dev;
}

const fixedCfApiUrl = pickApiBase(
  "VITE_DEV_CF_API_URL",
  "VITE_STAGING_CF_API_URL",
  "VITE_STABLE_CF_API_URL"
);
const fixedLbApiUrl = pickApiBase(
  "VITE_DEV_LB_API_URL",
  "VITE_STAGING_LB_API_URL",
  "VITE_STABLE_LB_API_URL"
);
const recaptchaSiteKey =
  import.meta.env.VITE_RECAPTCHA_SITE_KEY ||
  "6Lejj1QrAAAAAD1PKflV2CuqFwdv2igU-j-Kh441";
const recaptchaAction =
  import.meta.env.VITE_RECAPTCHA_ACTION || "checkout_purchase";
let recaptchaEnterpriseLoadPromise = null;
const CARD_ATTEMPT_LIMIT = 3;
const CARD_ATTEMPT_WINDOW_MS = 30 * 60 * 1000;
const CARD_ATTEMPT_BLOCK_MESSAGE =
  "Too many payment attempts were detected.\nPlease wait 30 minutes before trying again.";

function getCardAttemptStorageKey(visitorId) {
  return `atomicpay:card-attempts:${visitorId || "anonymous"}`;
}

function getLocalCardAttemptState(visitorId) {
  try {
    const raw = localStorage.getItem(getCardAttemptStorageKey(visitorId));
    if (!raw) return { count: 0, blockedUntil: 0 };

    const parsed = JSON.parse(raw);
    if (Number(parsed.expiresAt || 0) <= Date.now()) {
      localStorage.removeItem(getCardAttemptStorageKey(visitorId));
      return { count: 0, blockedUntil: 0 };
    }

    return parsed;
  } catch {
    return { count: 0, blockedUntil: 0 };
  }
}

function getLocalCardAttemptBlock(visitorId) {
  const state = getLocalCardAttemptState(visitorId);
  const blockedUntil = Number(state.blockedUntil || 0);

  if (blockedUntil > Date.now()) {
    return {
      blocked: true,
      retryAfterSeconds: Math.ceil((blockedUntil - Date.now()) / 1000),
    };
  }

  return { blocked: false };
}

function recordLocalCardAttemptFailure(visitorId) {
  const state = getLocalCardAttemptState(visitorId);
  const count = Number(state.count || 0) + 1;
  const blockedUntil =
    count >= CARD_ATTEMPT_LIMIT ? Date.now() + CARD_ATTEMPT_WINDOW_MS : 0;

  localStorage.setItem(
    getCardAttemptStorageKey(visitorId),
    JSON.stringify({
      count,
      blockedUntil,
      expiresAt: Date.now() + CARD_ATTEMPT_WINDOW_MS,
    })
  );

  return { count, blockedUntil };
}

function clearLocalCardAttemptFailures(visitorId) {
  localStorage.removeItem(getCardAttemptStorageKey(visitorId));
}

function loadRecaptchaEnterprise() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  if (window.grecaptcha?.enterprise) {
    return Promise.resolve();
  }

  if (recaptchaEnterpriseLoadPromise) {
    return recaptchaEnterpriseLoadPromise;
  }

  recaptchaEnterpriseLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(
      recaptchaSiteKey
    )}`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () =>
      reject(new Error("Failed to load reCAPTCHA Enterprise script"));
    document.head.appendChild(script);
  });

  return recaptchaEnterpriseLoadPromise;
}

async function executeRecaptchaEnterprise() {
  await loadRecaptchaEnterprise();

  if (!window.grecaptcha?.enterprise) {
    throw new Error("reCAPTCHA Enterprise is not available");
  }

  await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(resolve);
  });

  return window.grecaptcha.enterprise.execute(recaptchaSiteKey, {
    action: recaptchaAction,
  });
}

export default function Checkout() {
  // Mobile detection
  const isMobile = useIsMobile();

  let visitorId = localStorage.getItem("visitorId");
  const sessionObject = localStorage.getItem("sessionId");
  let sessionId = sessionObject ? JSON.parse(sessionObject).id : null;

  if (!visitorId) {
    visitorId = getUniqueId(15);
    localStorage.setItem("visitorId", visitorId);
  }

  if (!sessionId) {
    sessionId = getUniqueId(8);
    localStorage.setItem(
      "sessionId",
      JSON.stringify({ id: sessionId, createdAt: new Date().getTime() })
    );
  } else if (
    sessionId &&
    sessionObject.createdAt < Date.now() - 1000 * 60 * 30
  ) {
    localStorage.removeItem("sessionId");
    sessionId = getUniqueId(8);
    localStorage.setItem(
      "sessionId",
      JSON.stringify({ id: sessionId, createdAt: new Date().getTime() })
    );
  }
  const query = useQuery();
  const checkoutDesignCode = query.get("cd");
  const upgradeLinkCode = query.get("upg");
  const embedCheckout = query.get("embed") === "1";
  const { id: checkoutUrlCode } = useParams();
  const [errorLoadingCheckout, setErrorLoadingCheckout] = useState(false);
  const [product, setProduct] = useState({});
  const [plan, setPlan] = useState({});
  const [funnel, setFunnel] = useState({});
  const [offer, setOffer] = useState({
    code: "",
    description: "",
    valueInCents: 0,
    paymentMethods: "",
    maxInstallments: 0,
    status: null,
    period: "",
  });
  const [availableForPurchase, setAvailableForPurchase] = useState({
    what: "",
    status: "loading",
  });
  const [pixels, setPixels] = useState({ facebookIds: [] });
  const [checkoutDesign, setCheckoutDesign] = useState({});
  const [apiDoc, setApiDoc] = useState(null);
  const [altApiDoc, setAltApiDoc] = useState(null);
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [checkoutSession, setCheckoutSession] = useState(null);
  const checkoutFetchCount = useRef(0);
  const [exceededFetchCount, setExceededFetchCount] = useState(false);
  const [upgradeLink, setUpgradeLink] = useState(null);
  const fetchInProgress = useRef(false);
  const abortControllerRef = useRef(null);
  const emailRef = useRef(null);
  const logoRef = useRef(null);
  const [orderBumps, setOrderBumps] = useState([]);
  const [orderBumpAcceptedByKey, setOrderBumpAcceptedByKey] = useState({});
  const isLocalhost = window.location.hostname === "localhost";

  function readJsonCookie(name) {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.trim().split("=");
      if (cookieName === name) {
        try {
          return JSON.parse(decodeURIComponent(cookieValue));
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  function buildCheckoutSessionContext(checkout = {}) {
    const queryParams = new URLSearchParams(window.location.search);
    const utm = {};
    const utmSource = queryParams.get("utm_source");
    const fbclid = queryParams.get("fbclid");

    if (fbclid && !utmSource) {
      utm.source = "facebook";
    } else if (utmSource) {
      utm.source = utmSource;
    }

    if (queryParams.get("utm_medium")) utm.medium = queryParams.get("utm_medium");
    if (queryParams.get("utm_campaign")) utm.campaign = queryParams.get("utm_campaign");
    if (queryParams.get("utm_content")) utm.content = queryParams.get("utm_content");
    if (queryParams.get("utm_term")) utm.term = queryParams.get("utm_term");

    const originPayload = {};
    if (Object.keys(utm).length > 0) originPayload.utm = utm;
    if (fbclid) originPayload.fbclid = fbclid;
    if (queryParams.get("xcode")) originPayload.xcode = queryParams.get("xcode");
    if (queryParams.get("src")) originPayload.src = queryParams.get("src");
    if (queryParams.get("sck")) originPayload.sck = queryParams.get("sck");
    if (queryParams.get("sa")) originPayload.sa = queryParams.get("sa");

    return {
      checkoutUrlCode,
      checkoutDesignCode,
      upgradeLinkCode,
      visitorId,
      sessionId,
      currentUrl: window.location.href,
      referrer: document.referrer || "",
      origin: originPayload,
      originJourney: readJsonCookie("user_journey"),
      funnelId: checkout.funnel?.id || null,
      affiliateId: affiliate?.id || null,
    };
  }

  async function requestCheckoutSession(checkout = {}) {
    try {
      const response = await fetchWithTimeout(`${fixedLbApiUrl}/checkout/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildCheckoutSessionContext(checkout)),
      });

      if (!response.ok) {
        throw new Error(`Failed to create checkout session: ${response.status}`);
      }

      const result = await response.json();
      if (result?.checkoutSession?.token) {
        setCheckoutSession(result.checkoutSession);
      }
    } catch (error) {
      if (!isLocalhost && !isTransientNetworkError(error)) {
        logException(error, {
          area: "requestCheckoutSession",
          tags: { component: "Checkout" },
          level: "warning",
        });
      }
      setCheckoutSession(null);
    }
  }

  async function fetchData(retry = false) {
    // Prevent concurrent fetches
    if (fetchInProgress.current) {
      return;
    }

    if (
      pendingPurchaseDoc?.status === "approved" ||
      pendingPurchaseDoc?.status === "trial_started"
    ) {
      return;
    }

    // Check if we've exceeded max retries
    if (checkoutFetchCount.current >= 3) {
      setExceededFetchCount(true);
      return;
    }

    // Abort any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    fetchInProgress.current = true;
    abortControllerRef.current = new AbortController();

    if (retry) {
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.min(
        1000 * Math.pow(2, checkoutFetchCount.current),
        4000
      );
      await sleep(delay);
    }

    checkoutFetchCount.current++;

    let url = `${fixedCfApiUrl}/checkout/${checkoutUrlCode}`;

    const params = new URLSearchParams();
    if (checkoutDesignCode) params.append("cd", checkoutDesignCode);
    if (upgradeLinkCode) params.append("upg", upgradeLinkCode);

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        cache: "no-store",
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.message === "Checkout URL not found") {
          setErrors({
            checkoutUrl: "not-found",
          });
        }
        throw new Error(`Failed to fetch checkout data: ${response.status}`);
      }

      const data = await response.json();
      const checkout = data?.checkout ?? data?.data?.checkout;
      const {
        activeApi,
        checkoutDesign: chkDesign,
        upgradeLink,
      } = data;
      if (!checkout) {
        throw new Error("Missing checkout in response");
      }

      const {
        product: p,
        offer: o,
        plan: pl,
        pixels: pxs,
        funnel: f,
      } = checkout;

      setProduct(p);
      setOffer(o);
      setPaymentProfile(checkout.payment || null);
      setPlan(pl || {});
      setFunnel(f || {});
      setPixels(pxs);
      setApiDoc(activeApi);
      setCheckoutReady(true);
      setCheckoutDesign(chkDesign);
      setErrorLoadingCheckout(false); // Clear error state on success
      requestCheckoutSession(checkout);

      const bumpsRaw = checkout.orderBumps ?? checkout.order_bumps;
      const bumpSingle = checkout.orderBump ?? checkout.order_bump;
      const bumpsList =
        bumpsRaw == null
          ? []
          : Array.isArray(bumpsRaw)
            ? bumpsRaw
            : typeof bumpsRaw === "object"
              ? Object.values(bumpsRaw)
              : [];
      const bumps =
        bumpsList.length > 0
          ? bumpsList
          : bumpSingle
            ? [bumpSingle]
            : [];
      setOrderBumps(bumps);
      setOrderBumpAcceptedByKey({});

      if (upgradeLink) {
        setUpgradeLink(upgradeLink);
        setEmail(upgradeLink.customerEmail);
      }

      const productIsInactive = p.status && p.status !== "active";
      const offerIsInactive = o.status !== "active";
      const planIsInactive =
        p.paymentModel === "subscription" && pl.status && pl.status !== "active";

      if (productIsInactive || offerIsInactive || planIsInactive) {
        logMessage("product, offer or plan is not active", {
          area: "fetchData",
          tags: { component: "Checkout" },
          extra: checkout,
        });

        console.log("product, offer or plan is not active", {
          product: p,
          offer: o,
          plan: pl,
        });

        setAvailableForPurchase({
          what: "product",
          status: "inactive",
        });
      }
    } catch (error) {
      // Don't log aborts or transient client/network failures.
      if (
        error.name !== "AbortError" &&
        !isTransientNetworkError(error)
      ) {
        logException(error, {
          area: "fetchData",
          tags: { component: "Checkout" },
          extra: {
            attempt: checkoutFetchCount.current,
            url,
            message: error.message,
          },
        });
      }
      if (error.name !== "AbortError") {
        setErrorLoadingCheckout(true);
      }
    } finally {
      fetchInProgress.current = false;
    }
  }

  // Single useEffect for initial fetch and retries
  useEffect(() => {
    if (checkoutReady) return;

    if (!errorLoadingCheckout && checkoutFetchCount.current === 0) {
      // Initial fetch
      fetchData();
    } else if (errorLoadingCheckout && checkoutFetchCount.current < 3) {
      // Retry on error
      fetchData(true);
    }

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    checkoutUrlCode,
    checkoutDesignCode,
    upgradeLinkCode,
    errorLoadingCheckout,
  ]);

  const visitId = getUniqueId(8);

  const affiliate = useMemo(() => {
    return getAffiliate(product.id);
  }, [product]);

  // Unique gradient IDs for SVGs
  const gradientIds = useMemo(
    () => ({
      creditCard: `creditCardGradient-${visitId}`,
      pix: `pixGradient-${visitId}`,
    }),
    [visitId]
  );

  const [pendingPurchaseId, setPendingPurchaseId] = useState(
    () =>
      new URLSearchParams(window.location.search).get("stripe_purchase_id") ||
      ""
  );
  const {
    purchaseData: pendingPurchaseDoc,
    loading: purchaseLoading,
    fetchCount,
    error: pendingPurchaseError,
  } = usePurchaseStatus(pendingPurchaseId, fixedCfApiUrl, funnel?.id ?? null);
  const [origin, setOrigin] = useState({});
  const [originJourney, setOriginJourney] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [paymentProfile, setPaymentProfile] = useState(null);
  const [stripeSession, setStripeSession] = useState(null);
  const isStripeCheckout = paymentProfile?.provider === "stripe";
  const [phoneNumber, setPhoneNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("credit_card");
  const [creditCard, setCreditCard] = useState({
    holderName: "",
    number: "",
    expDate: "",
    cvv: "",
  });
  const [saveDataForNextPurchases, setSaveDataForNextPurchases] =
    useState(true);
  const [installments, setInstallments] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [purchaseResultBackup, setPurchaseResultBackup] = useState(null);
  const recordedCardFailureKeysRef = useRef(new Set());
  const [qrcodeImage, setQrcodeImage] = useState(undefined);
  const [qrcodeImageBackup, setQrcodeImageBackup] = useState(undefined);
  const [qrcode, setQrcode] = useState(undefined);
  const [expirationTime, setExpirationTime] = useState(null);
  const [pixExpired, setPixExpired] = useState(false);
  const [errors, setErrors] = useState({});
  const [fieldsUpdated, setFieldsUpdated] = useState({
    email: false,
    name: false,
    phoneNumber: false,
  });
  const [hoveredButton, setHoveredButton] = useState(null);
  const pixelInitialized = useRef(new Set()); // ✅ Armazena IDs já inicializados para evitar repetição

  const recordBackendCardFailure = (failureKey) => {
    if (!failureKey || recordedCardFailureKeysRef.current.has(failureKey)) {
      return;
    }

    recordedCardFailureKeysRef.current.add(failureKey);
    recordLocalCardAttemptFailure(visitorId);
  };

  useEffect(() => {
    if (!pendingPurchaseDoc) return;

    if (pendingPurchaseError) {
      if (pendingPurchaseError.code === "pix-generation-failed") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          pix: {
            error_code: "pix-generation-failed",
            error_type: "Pix não gerado",
            error_message: pendingPurchaseError.message,
          },
        }));
      }
      logException(pendingPurchaseError, {
        area: "usePurchaseStatus",
        tags: { component: "Checkout" },
        extra: { purchaseId: pendingPurchaseId },
      });
    }
  }, [pendingPurchaseDoc, pendingPurchaseError]);

  useEffect(() => {
    if (affiliate && affiliate.where === "url" && product?.id) {
      setCookie(`aff_${product.id}`, affiliate.id, 30);
    }
  }, [affiliate, product]);

  const handleEmailChange = (e) => {
    const value = e.target.value.trim().toLowerCase();
    // Remove caracteres inválidos
    const validEmail = isLocalhost ? value : value.replace(/[^a-z0-9@._-]/g, "");
    setEmail(validEmail);
    setErrors((prevErrors) => ({ ...prevErrors, email: "" })); // Limpa a mensagem de erro ao digitar
  };

  const handleEmailBlur = async (value) => {
    if (!isValidEmail(email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Digite um e-mail válido",
      }));
      return;
    }

    if (!fieldsUpdated["email"] && product.id) {
      setFieldsUpdated((prevFields) => ({ ...prevFields, email: true }));

      await sendPixelEvent(fixedLbApiUrl, {
        productId: product.id,
        events: [
          {
            productId: product.id,
            event_name: "PageView",
            event_id: `${visitorId}-${visitId}`,
            event_source_url: window.location.href,
            action_source: "website",
            user_agent: navigator.userAgent,
            userId: visitorId,
            fbp: getCookie("_fbp"),
            fbc: getCookie("_fbc"),
            currency: "BRL",
            value: (offer.valueInCents / 100).toFixed(2),
          },
          {
            productId: product.id,
            event_name: "InitiateCheckout",
            event_id: `${visitorId}-${visitId}`,
            event_source_url: window.location.href,
            action_source: "website",
            user_agent: navigator.userAgent,
            userId: visitorId,
            fbp: getCookie("_fbp"),
            fbc: getCookie("_fbc"),
            currency: "BRL",
            value: (offer.valueInCents / 100).toFixed(2),
          },
        ],
      });
    }

    // Verifica se o e-mail já foi cadastrado TODO
    // const response = await fetch(`https://api.example.com/check-email`, {
    //   method: "POST",
    //   headers: {
    //     "Content-Type": "application/json",
    //   },
    //   body: JSON.stringify({ email }),
    // });

    // const result = await response.json();
    // envia sms, confirma e preenche com os dados
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    const formattedValue = formatName(value);
    setName(formattedValue);
    if (
      isValidName(formattedValue) ||
      isValidBrazilianCompanyName(formattedValue)
    ) {
      setErrors((prevErrors) => ({ ...prevErrors, name: "" }));
    }
  };

  const handleNameBlur = async (value) => {
    if (!isValidName(name) && !isValidBrazilianCompanyName(name)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        name: "Digite um nome ou razão social válido",
      }));
      return;
    }

    if (!fieldsUpdated["name"] && product.id) {
      setFieldsUpdated((prevFields) => ({ ...prevFields, name: true }));
      await sendPixelEvent(fixedLbApiUrl, {
        productId: product.id,
        events: [
          {
            productId: product.id,
            event_name: "PageView",
            event_id: `${visitorId}-${visitId}`,
            event_source_url: window.location.href,
            action_source: "website",
            user_agent: navigator.userAgent,
            userId: visitorId,
            fbp: getCookie("_fbp"),
            fbc: getCookie("_fbc"),
            currency: "BRL",
            value: (offer.valueInCents / 100).toFixed(2),
          },
          {
            productId: product.id,
            event_name: "InitiateCheckout",
            event_id: `${visitorId}-${visitId}`,
            event_source_url: window.location.href,
            action_source: "website",
            user_agent: navigator.userAgent,
            userId: visitorId,
            fbp: getCookie("_fbp"),
            fbc: getCookie("_fbc"),
            currency: "BRL",
            value: (offer.valueInCents / 100).toFixed(2),
          },
        ],
      });
    }
  };

  const handleHolderNameChange = (e) => {
    const value = e.target.value;
    const formattedValue = value.replace(/[^a-zA-Z\s]/g, "").toUpperCase();
    setCreditCard({ ...creditCard, holderName: formattedValue });
  };

  const handleTaxIdChange = (e) => {
    const value = e.target.value;
    const formattedValue = formatTaxId(value);
    setTaxId(formattedValue);
    const isValidTaxId =
      isValidCPF(formattedValue) || isValidCNPJ(formattedValue);

    if (isValidTaxId) {
      setErrors((prevErrors) => ({ ...prevErrors, taxId: "" }));
    }
  };

  const handleTaxIdBlur = () => {
    if (!isValidCPF(taxId) && !isValidCNPJ(taxId)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        taxId: "Digite um CPF ou CNPJ válido",
      }));
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const formattedValue = formatPhoneNumber(value);
    setPhoneNumber(formattedValue);

    if (isValidBrazilianPhone(formattedValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, phoneNumber: "" }));
    }
  };

  const handlePhoneNumberBlur = async (value) => {
    if (!isValidBrazilianPhone(phoneNumber)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phoneNumber: "Digite um número de celular válido",
      }));
      return;
    }

    if (!fieldsUpdated["phoneNumber"] && product.id) {
      setFieldsUpdated((prevFields) => ({ ...prevFields, phoneNumber: true }));

      await sendPixelEvent(fixedLbApiUrl, {
        productId: product.id,
        events: [
          {
            productId: product.id,
            event_name: "PageView",
            event_id: `${visitorId}-${visitId}`,
            event_source_url: window.location.href,
            action_source: "website",
            user_agent: navigator.userAgent,
            userId: visitorId,
            fbp: getCookie("_fbp"),
            fbc: getCookie("_fbc"),
            currency: "BRL",
            value: (offer.valueInCents / 100).toFixed(2),
          },
          {
            productId: product.id,
            event_name: "InitiateCheckout",
            event_id: `${visitorId}-${visitId}`,
            event_source_url: window.location.href,
            action_source: "website",
            user_agent: navigator.userAgent,
            userId: visitorId,
            fbp: getCookie("_fbp"),
            fbc: getCookie("_fbc"),
            currency: "BRL",
            value: (offer.valueInCents / 100).toFixed(2),
          },
        ],
      });
    }
  };

  const handleExpDateChange = (e) => {
    const value = e.target.value;
    const formattedValue = formatExpDate(value);
    setCreditCard({
      ...creditCard,
      expDate: formattedValue,
    });
  };

  const handleCVVChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setCreditCard((prevCard) => ({
      ...prevCard,
      cvv: value,
    }));
  };

  const cardType = detectCardType(creditCard.number);

  const handleCreditCardNumberChange = (e) => {
    const value = e.target.value;
    const formattedValue = formatCreditCardNumber(value);
    setCreditCard((prevCard) => ({ ...prevCard, number: formattedValue }));
    setErrors((prevErrors) => ({ ...prevErrors, creditCardNumber: "" })); // Limpa a mensagem de erro ao digitar
  };

  const handleCreditCardNumberBlur = () => {
    if (!isValidCreditCardNumber(creditCard.number)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        creditCardNumber: "Digite um número de cartão válido",
      }));
    }
  };

  const handleExpDateBlur = () => {
    const expDateArray = creditCard.expDate.split("/");
    if (
      creditCard.expDate.length !== 5 ||
      expDateArray.length !== 2 ||
      Number(expDateArray[0]) > 12 ||
      Number(expDateArray[0]) < 1 ||
      Number(expDateArray[1]) <
        Number(new Date().getFullYear().toString().slice(2)) ||
      Number(expDateArray[1]) >
        Number(new Date().getFullYear().toString().slice(2)) + 10
    ) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        expDate: "Digite uma data de expiração válida.",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, expDate: "" }));
    }
  };

  const scrollToCheckoutStart = () => {
    const target =
      logoRef.current ||
      emailRef.current ||
      document.getElementById("checkout-form") ||
      document.getElementById("checkout-core");

    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const checkAllFields = () => {
    let allFieldsAreValid = true;
    let count = 0;

    if (!isValidEmail(email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Digite um e-mail válido",
      }));
      allFieldsAreValid = false;
      count += 1;
      scrollToCheckoutStart();
    }

    if (!isValidName(name) && !isValidBrazilianCompanyName(name)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        name: "Digite um nome ou razão social válido",
      }));
      allFieldsAreValid = false;
      count += 1;
      scrollToCheckoutStart();
    }

    if (count === 2) {
      return allFieldsAreValid;
    }

    if (!isStripeCheckout && !isValidCPF(taxId) && !isValidCNPJ(taxId)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        taxId: "Digite um CPF ou CNPJ válido",
      }));
      allFieldsAreValid = false;
      count += 1;
      scrollToCheckoutStart();
    }

    if (count === 2) {
      return allFieldsAreValid;
    }

    if (!isStripeCheckout && !isValidBrazilianPhone(phoneNumber)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        phoneNumber: "Digite um número de celular válido",
      }));
      allFieldsAreValid = false;
      count += 1;
      scrollToCheckoutStart();
    }

    if (count === 2) {
      return allFieldsAreValid;
    }

    if (!isStripeCheckout && paymentMethod === "credit_card") {
      if (!creditCard.holderName) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          holderName: "Digite o nome do titular do cartão",
        }));
        allFieldsAreValid = false;
      }

      if (!isValidCreditCardNumber(creditCard.number)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          creditCardNumber: "Digite um número de cartão válido",
        }));
        allFieldsAreValid = false;
        count += 1;
      }

      const expDateArray = creditCard.expDate.split("/");

      if (
        creditCard.expDate.length !== 5 ||
        expDateArray.length !== 2 ||
        Number(expDateArray[0]) > 12 ||
        Number(expDateArray[0]) < 1 ||
        Number(expDateArray[1]) <
          Number(new Date().getFullYear().toString().slice(2)) ||
        Number(expDateArray[1]) >
          Number(new Date().getFullYear().toString().slice(2)) + 10
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          expDate: "Digite uma data de expiração válida",
        }));
        allFieldsAreValid = false;
        count += 1;
      }

      if (creditCard.cvv.length !== 3 && creditCard.cvv.length !== 4) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          cvv: "Digite um CVV válido",
        }));
        allFieldsAreValid = false;
        count += 1;
      }
    }

    return allFieldsAreValid;
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  /* ---------------------- fetch with timeout helper ---------------------- */
  const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(id);
      return response;
    } catch (error) {
      clearTimeout(id);
      throw error;
    }
  };

  /* ---------------------- reCAPTCHA helpers ---------------------- */

  /**
   * Tries to obtain a fresh reCAPTCHA Enterprise token for the checkout
   * purchase action. If it fails, the backend will reject the purchase.
   */
  const getRecaptchaToken = async () => {
    try {
      return await executeRecaptchaEnterprise();
    } catch (error) {
      logException(error, {
        area: "Recaptcha",
        tags: { component: "Checkout" },
        level: "warning",
      });
      return null;
    }
  };

  /**
   * Main entry-point when the buyer clicks "Pagar agora" / "Gerar Pix".
   * We request a reCAPTCHA Enterprise action token and send it to the backend
   * so Google can score the purchase attempt.
   */
  const processPurchase = async () => {
    // Clear any previous error banners so the user gets fresh feedback for the new attempt
    setErrors({});
    setIsPending(true);

    const allFieldsAreValid = checkAllFields();
    if (!allFieldsAreValid) {
      // Revert loading state if validation fails
      setIsPending(false);
      return;
    }

    if (paymentMethod === "credit_card") {
      const localCardBlock = getLocalCardAttemptBlock(visitorId);
      if (localCardBlock.blocked) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          card: {
            error_type: "Too many payment attempts",
            error_message: CARD_ATTEMPT_BLOCK_MESSAGE,
          },
        }));
        setIsPending(false);
        return;
      }
    }

    const recaptchaToken = await getRecaptchaToken();

    let pixelsData = null;
    if (pixels) {
      pixelsData = {
        facebook: {
          productId: product.id,
          event_id: `${visitorId}-${visitId}`,
          event_source_url: window.location.href,
          action_source: "website",
          user_agent: navigator.userAgent,
          userId: visitorId,
          email: email,
          phone: phoneNumber,
          name: name,
          fbp: getCookie("_fbp"),
          fbc: getCookie("_fbc"),
        },
      };
    }

    if (isStripeCheckout) {
      await processStripe(pixelsData, recaptchaToken);
      return;
    }

    if (paymentMethod === "credit_card") {
      try {
        // Get Pagar.me token for the card
        const tokenizeCardUrl =
          "https://api.pagar.me/core/v5/tokens?appId=pk_dzr5yBqtgQFvM7k0";

        const tokenizeCardResponse = await fetch(tokenizeCardUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
          },
          body: JSON.stringify({
            type: "card",
            card: {
              number: creditCard.number.replace(/\D/g, ""),
              holder_name: creditCard.holderName,
              exp_month: creditCard.expDate.split("/")[0],
              exp_year: creditCard.expDate.split("/")[1],
              cvv: creditCard.cvv,
            },
          }),
        });

        const tokenizeCardData = await tokenizeCardResponse.json().catch(() => ({}));

        if (!tokenizeCardResponse.ok) {
          const pagarmeMessage =
            tokenizeCardData?.message ||
            tokenizeCardData?.errors?.[0]?.message ||
            tokenizeCardData?.errors?.[0]?.type;
          const tokenizeError = new Error(
            pagarmeMessage || "Failed to tokenize card"
          );
          tokenizeError.isCardValidationError =
            tokenizeCardResponse.status >= 400 &&
            tokenizeCardResponse.status < 500;
          throw tokenizeError;
        }

        const pagarmeToken = tokenizeCardData?.id;

        await processCard(pixelsData, pagarmeToken, recaptchaToken);
      } catch (error) {
        const attemptState = recordLocalCardAttemptFailure(visitorId);
        const blocked = Number(attemptState.blockedUntil || 0) > Date.now();
        const isUserCardError =
          error?.isCardValidationError || isTransientNetworkError(error);

        if (!isUserCardError) {
          logException(error, {
            area: "tokenizeCard",
            tags: { component: "Checkout", payment_method: "credit_card" },
          });
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          card: {
            error_description: blocked
              ? "Too many payment attempts"
              : "Error while processing card",
            error_type: blocked
              ? "Too many payment attempts"
              : "Transação não iniciada",
            error_message: blocked
              ? CARD_ATTEMPT_BLOCK_MESSAGE
              : "Verifique os dados do cartão e tente novamente.",
          },
        }));
        setIsPending(false);
      }
    } else {
      await processPix(pixelsData, recaptchaToken);
    }
  };

  const processStripe = async (pixelsData, recaptchaToken) => {
    if (stripeSession || pendingPurchaseId) return;
    if (paymentProfile?.available === false) {
      setErrors((prev) => ({
        ...prev,
        card: {
          error_type: "Pagamento indisponível",
          error_message: "Este checkout internacional não está pronto para receber pagamentos.",
        },
      }));
      setIsPending(false);
      return;
    }
    setIsPending(true);
    try {
      const response = await fetchWithTimeout(`${fixedLbApiUrl}/order/initiate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer: { email, name, phoneNumber },
          product: {
            plan: plan.code || null,
            offer: offer.code,
            id: product.id,
            name: product.name,
          },
          purchase: {
            paymentMethod: "credit_card",
            checkoutUrlCode: checkoutUrlCode || undefined,
            checkoutSessionToken: checkoutSession?.token || null,
            checkoutSessionId: checkoutSession?.id || null,
            origin,
            originJourney,
            pixelsData,
            firstCharge: true,
            sessionId,
            visitorId,
            funnelId: funnel?.id || null,
            recaptchaToken,
          },
        }),
      });
      const result = await response.json();
      if (
        !response.ok ||
        (!result.clientSecret && result.status !== "processing")
      ) {
        throw new Error(result.message || "Não foi possível iniciar o pagamento");
      }
      setStripeSession(result.clientSecret ? result : null);
      setPurchaseResult({
        purchaseId: result.purchaseId,
        status: "initiated",
      });
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        card: {
          error_type: "Pagamento não iniciado",
          error_message: error.message,
        },
      }));
    } finally {
      setIsPending(false);
    }
  };

  const processPix = async (pixelsData, recaptchaToken) => {
    while (!checkoutReady) {
      await sleep(1000);
    }

    try {
      const response = await fetchWithTimeout(
        `${fixedLbApiUrl}/order/initiate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customer: {
              email,
              name,
              taxId,
              phoneNumber,
            },
            product: {
              plan: plan.code || null,
              offer: offer.code,
              id: product.id,
              name: product.name,
            },
            purchase: {
              paymentMethod,
              checkoutUrlCode: checkoutUrlCode || undefined,
              checkoutSessionToken: checkoutSession?.token || null,
              checkoutSessionId: checkoutSession?.id || null,
              installments,
              origin,
              originJourney,
              pixelsData,
              firstCharge: true, // TODO: Temos que detectar se é ou não
              affiliateId: affiliate?.id,
              affiliateSource: affiliate?.where || null,
              sessionId: sessionId,
              funnelId: funnel?.id || null,
              orderBumpsAccepted: getAcceptedOrderBumps(),
              discounts: {
                upgrade: upgradeLinkCode
                  ? {
                      code: upgradeLinkCode,
                    }
                  : null,
              },
              recaptchaToken,
            },
            additionalInfo: {
              saveDataForNextPurchases,
            },
          }),
        }
      );

      const result = await response.json();

      setPurchaseResult(result);
    } catch (error) {
      if (error.name === "AbortError") {
        // Timeout reached – show friendly message
        setErrors((prevErrors) => ({
          ...prevErrors,
          pix: {
            error_type: "Tempo esgotado",
            error_message:
              "Não foi possível gerar o Pix no tempo esperado. Tente novamente.",
          },
        }));
      } else {
        if (!isTransientNetworkError(error)) {
          logException(error, {
            area: "processPix",
            tags: { component: "Checkout", payment_method: "pix" },
          });
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          pix: {
            error_type: "Pix não gerado",
            error_message: error.message,
          },
        }));
      }
      setPixExpired(false);
      setIsPending(false);
    }
  };

  const processCard = async (pixelsData, pagarmeToken, recaptchaToken) => {
    setIsPending(true);

    try {
      const response = await fetchWithTimeout(
        `${fixedLbApiUrl}/order/initiate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customer: {
              email,
              name,
              taxId,
              phoneNumber,
            },
            product: {
              plan: plan.code || null,
              offer: offer.code,
              id: product.id,
              name: product.name,
            },
            purchase: {
              paymentMethod,
              creditCard: {
                token: pagarmeToken,
              },
              checkoutUrlCode: checkoutUrlCode || undefined,
              checkoutSessionToken: checkoutSession?.token || null,
              checkoutSessionId: checkoutSession?.id || null,
              installments,
              origin,
              originJourney,
              pixelsData,
              firstCharge: true,
              affiliateId: affiliate?.id,
              affiliateSource: affiliate?.where || null,
              visitorId,
              deviceId: visitorId,
              sessionId: sessionId,
              funnelId: funnel?.id || null,
              orderBumpsAccepted: getAcceptedOrderBumps(),
              discounts: {
                upgrade: upgradeLinkCode
                  ? {
                      code: upgradeLinkCode,
                    }
                  : null,
              },
              recaptchaToken,
            },
            additionalInfo: {
              saveDataForNextPurchases,
            },
          }),
        }
      );

      const result = await response.json();

      setPurchaseResult(result);
      setPixExpired(false);
    } catch (err) {
      if (err.name === "AbortError") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          card: {
            error_description: "Tempo esgotado",
            error_type: "Transação não iniciada",
            error_message:
              "A solicitação demorou demais. Por favor, tente novamente.",
          },
        }));
      } else {
        if (!isTransientNetworkError(err)) {
          logException(err, {
            area: "processCard",
            tags: { component: "Checkout", payment_method: "credit_card" },
          });
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          card: {
            error_description: "Error while processing card",
            error_type: "Transação não iniciada",
            error_message: "Verifique os dados do cartão e tente novamente.",
          },
        }));
      }
      setPixExpired(false);
      setIsPending(false);
    }
  };

  const hexToRgba = (hex, alpha) => {
    if (hex.includes("rgb(")) {
      return `rgba(${hex.replace("rgb(", "").replace(")", "")}, ${alpha})`;
    }

    if (hex.includes("rgba(")) {
      return hex;
    }

    if (!hex) return "rgba(0, 0, 0, 0)";
    if (hex.startsWith("#")) {
      hex = hex.slice(1);
    }
    const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const installmentsOptions = getInstallments(offer.valueInCents, offer.period);

  // Memoize availableInstallments to prevent unnecessary recalculations
  const availableInstallments = useMemo(() => {
    // If we have an upgrade discount, recalculate installments with the discounted price
    if (upgradeLink?.upgrade) {
      const finalValueInCents = upgradeLink.upgrade.finalValue;
      // Recalculate installments with the discounted price to include proper interest
      return getInstallments(finalValueInCents, offer.period);
    }

    // Otherwise use the original installments
    return installmentsOptions;
  }, [installmentsOptions, upgradeLink?.upgrade, offer.period]);

  const getSelectedInstallmentsValue = () => {
    // Since availableInstallments already has the correct values with interest,
    // we can just use them directly
    const selectedInstallment = availableInstallments.find(
      (installment) => installment.qtt === Number(installments)
    );

    return selectedInstallment?.value || 12;
  };

  const getAcceptedOrderBumps = () => {
    if (
      upgradeLink?.upgrade ||
      Number(offer?.trial) > 0
    ) {
      return [];
    }

    return orderBumps
      .filter((bump) => {
        const key = `${bump.product?.id}|${bump.offer?.code}`;
        return orderBumpAcceptedByKey[key];
      })
      .map((bump) => ({
        productId: bump.product.id,
        offerCode: bump.offer.code,
      }));
  };

  const getAcceptedOrderBumpTotal = () => {
    if (
      upgradeLink?.upgrade ||
      Number(offer?.trial) > 0
    ) {
      return 0;
    }

    return orderBumps.reduce((total, bump) => {
      const key = `${bump.product?.id}|${bump.offer?.code}`;
      if (!orderBumpAcceptedByKey[key]) {
        return total;
      }

      return total + (Number(bump.offer?.valueInCents) || 0) / 100;
    }, 0);
  };

  const getPixBaseValue = () => {
    if (upgradeLink?.upgrade) {
      return upgradeLink.upgrade.finalValue / 100;
    }

    return availableInstallments.length
      ? availableInstallments[availableInstallments.length - 1].value
      : offer.valueInCents / 100;
  };

  const getPixTotalValue = () => {
    return getPixBaseValue() + getAcceptedOrderBumpTotal();
  };

  const handleOrderBumpChange = (key, checked) => {
    setOrderBumpAcceptedByKey((prev) => ({
      ...prev,
      [key]: checked,
    }));

    if (paymentMethod === "pix") {
      setErrors({});
      setIsPending(false);
      setPurchaseResult(null);
      setPurchaseResultBackup(null);
      setPendingPurchaseId("");
      setQrcodeImage(undefined);
      setQrcodeImageBackup(undefined);
      setQrcode(undefined);
      setExpirationTime(null);
      setPixExpired(false);
    }
  };

  // Helper functions for price display
  const formatPrice = (value) => {
    // Ensure value is a number
    const numValue =
      typeof value === "string" ? parseFloat(value) : Number(value) || 0;
    return numValue.toFixed(2).replace(".", ",");
  };

  const getUpgradePrice = (originalValue) => {
    if (!upgradeLink?.upgrade) return originalValue;

    // Ensure we're working with numbers
    const numOriginalValue =
      typeof originalValue === "string"
        ? parseFloat(originalValue)
        : Number(originalValue) || 0;
    const discountValue = upgradeLink.upgrade.discount / 100;
    return numOriginalValue - discountValue;
  };

  const getDiscountPercentage = () => {
    if (!upgradeLink?.upgrade || !offer.valueInCents) return 0;

    const originalValue = offer.valueInCents / 100;
    const discountValue = upgradeLink.upgrade.discount / 100;
    const percentage = (discountValue / originalValue) * 100;

    return Math.round(percentage);
  };

  const renderCreditCardPricing = () => {
    // Safety check for installmentsOptions
    if (!availableInstallments || availableInstallments.length === 0) {
      return renderPricingSkeleton();
    }

    const isHighValue = availableInstallments[0].value > 5;
    const hasUpgrade = upgradeLink?.upgrade;
    const hasTrial = offer?.trial > 0;

    if (isHighValue) {
      // Get the current selected installment from availableInstallments
      const selectedInstallment =
        availableInstallments.find(
          (inst) => inst.qtt === Number(installments)
        ) || availableInstallments[0];

      const upgradeInstallmentValue = selectedInstallment.value;

      return (
        <div className="min-w-[150px]">
          <h2
            className="text-lg sm:text-xl font-medium"
            style={{ color: theme.headingText }}
          >
            {offer?.period !== "monthly" ? (
              Number(installments) > 1 ? (
                <span>{installments}x de R$ </span>
              ) : (
                <span>R$ </span>
              )
            ) : (
              <span>R$ </span>
            )}
            {offer ? formatPrice(upgradeInstallmentValue) : ""}{" "}
            {getPeriod(offer)}
          </h2>
          {offer?.period !== "monthly" && (
            <p
              className="mt-1 text-xs sm:text-sm"
              style={{ color: theme.mutedText }}
            >
              {availableInstallments.length > 1 && (
                <>
                  Ou R${" "}
                  {offer
                    ? formatPrice(
                        availableInstallments[availableInstallments.length - 1]
                          .value
                      )
                    : ""}{" "}
                  {getPeriod(offer)} à vista
                </>
              )}
            </p>
          )}
          {hasTrial && (
            <p className="mt-1 text-blue-500 text-xs sm:text-sm font-medium">
              {offer.trial} dias grátis
            </p>
          )}
        </div>
      );
    }

    // Low value pricing
    const upgradeValue = availableInstallments.length
      ? availableInstallments[0].value
      : 0;

    return (
      <div className="flex gap-2.5 items-center">
        <h2
          className="text-lg sm:text-xl font-medium"
          style={{ color: theme.headingText }}
        >
          R$ {formatPrice(upgradeValue)}{" "}
        </h2>
        {hasTrial && (
          <span className="text-xs sm:text-sm text-blue-500 font-medium">
            • {offer.trial} dias grátis
          </span>
        )}
      </div>
    );
  };

  const renderPixPricing = () => {
    // Safety check for installmentsOptions
    if (!availableInstallments || availableInstallments.length === 0) {
      return renderPricingSkeleton();
    }

    const pixValue = getPixTotalValue();
    const orderBumpTotal = getAcceptedOrderBumpTotal();

    const hasTrial = offer?.trial > 0;

    return (
      <div>
        <h2
          className="text-lg sm:text-xl font-medium"
          style={{ color: theme.headingText }}
        >
          R$ {formatPrice(pixValue)} {getPeriod(offer)}
        </h2>
        <p
          className="mt-0.5 text-xs sm:text-sm"
          style={{ color: theme.mutedText }}
        >
          À vista no pix
        </p>
        {orderBumpTotal > 0 && (
          <p className="mt-0.5 text-xs sm:text-sm" style={{ color: theme.mutedText }}>
            Produto + ofertas adicionais
          </p>
        )}
        {hasTrial && (
          <p className="mt-0.5 text-blue-500 text-xs sm:text-sm font-medium">
            {offer.trial} dias grátis
          </p>
        )}
      </div>
    );
  };

  const renderPricingSkeleton = () => {
    return (
      <div className="flex flex-col gap-2.5">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  };

  const renderPricing = () => {
    if (!offer.code) {
      return renderPricingSkeleton();
    }

    if (paymentMethod === "credit_card") {
      return renderCreditCardPricing();
    }

    return renderPixPricing();
  };

  // ------ useEffects begin ------

  // Load Google Fonts when pageSettings change
  useEffect(() => {
    // Get page settings with fallback
    let fontFamily = null;

    if (isMobile) {
      const mobilePageSettings = checkoutDesign?.mobilePageSettings;
      if (mobilePageSettings && Object.keys(mobilePageSettings).length > 0) {
        fontFamily = mobilePageSettings.fontFamily;
      } else {
        // Fall back to desktop font if mobile settings don't exist
        fontFamily = checkoutDesign?.desktopPageSettings?.fontFamily;
      }
    } else {
      fontFamily = checkoutDesign?.desktopPageSettings?.fontFamily;
    }

    if (!fontFamily || fontFamily === "inherit") return;

    const linkId = `google-font-${fontFamily.replace(/\s+/g, "-")}`;

    // Check if font is already loaded
    if (!document.getElementById(linkId)) {
      const link = document.createElement("link");
      link.id = linkId;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
        /\s+/g,
        "+"
      )}:wght@400;500;600;700&display=swap`;
      document.head.appendChild(link);
    }
  }, [checkoutDesign, isMobile]);

  // Set initial installments and payment method
  useEffect(() => {
    if (!offer.code) return;

    // Only set initial installments if not already set
    if (
      installments === null &&
      availableInstallments &&
      availableInstallments.length > 0
    ) {
      setInstallments(availableInstallments[0].qtt);
    }

    if (offer.paymentMethods === "Apenas pix") {
      setPaymentMethod("pix");
    }
  }, [offer.code, offer.paymentMethods, availableInstallments]);

  useEffect(() => {
    if (!product?.id) return;
    loadCheckoutIntegrations(product.checkoutIntegrations);
  }, [product?.id, product?.checkoutIntegrations]);

  // Send pixel events once when offer and product are loaded
  useEffect(() => {
    if (!offer.code || !product.id || !pixels) return;

    if (!pixels?.facebookIds?.length) {
      return;
    }

    const sendPixelEvt = async (apiUrl, data) => {
      if (window.location.hostname === "localhost") {
        return;
      }

      if (!apiUrl) {
        return;
      }

      try {
        const response = await fetch(apiUrl + "/pixels/facebook", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        if (!response.ok) {
          throw new Error("Failed to send pixel event");
        }
      } catch (error) {
        logException(error, {
          area: "sendPixelEvt",
          tags: { component: "Checkout" },
        });
      }
    };

    const pixelsData = {
      productId: product.id,
      events: [
        {
          productId: product.id,
          event_name: "PageView",
          event_id: `${visitorId}-${visitId}`,
          event_source_url: window.location.href,
          action_source: "website",
          user_agent: navigator.userAgent,
          userId: visitorId,
          fbp: getCookie("_fbp"),
          fbc: getCookie("_fbc"),
          currency: "BRL",
          value: (offer.valueInCents / 100).toFixed(2),
        },
        {
          productId: product.id,
          event_name: "InitiateCheckout",
          event_id: `${visitorId}-${visitId}`,
          event_source_url: window.location.href,
          action_source: "website",
          user_agent: navigator.userAgent,
          userId: visitorId,
          fbp: getCookie("_fbp"),
          fbc: getCookie("_fbc"),
          currency: "BRL",
          value: (offer.valueInCents / 100).toFixed(2),
        },
      ],
    };

    sendPixelEvt(fixedLbApiUrl, pixelsData);
  }, [offer.code, product.id]); // Only run when offer and product are loaded

  useEffect(() => {
    if (!offer.code || !pixels?.facebookIds?.length) {
      return;
    }

    // Se o Pixel ainda não foi carregado, adiciona o script na página
    if (!window.fbq) {
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js"
      );
    }

    pixels.facebookIds.forEach((pixelId) => {
      if (!pixelInitialized.current.has(pixelId)) {
        // ✅ Se esse Pixel ainda não foi inicializado, inicializa e marca como feito
        window.fbq("init", pixelId);
        pixelInitialized.current.add(pixelId);
      }

      // ✅ Envia `PageView` para cada Pixel com mais dados
      window.fbq("track", "PageView", {
        event_id: `${visitorId}-${visitId}`,
        event_source_url: window.location.href,
        action_source: "website",
      });

      // ✅ Envia o evento de `InitiateCheckout` para cada Pixel
      window.fbq("track", "InitiateCheckout", {
        event_id: `${visitorId}-${visitId}`,
        currency: "BRL",
        value: (offer.valueInCents / 100).toFixed(2),
      });
    });
  }, [offer, pixels]);

  useEffect(() => {
    if (!offer.code || !pendingPurchaseDoc) return;

    if (
      pendingPurchaseDoc.status === "pix_generated" &&
      pendingPurchaseDoc.paymentMethod === "pix"
    ) {
      setQrcodeImage(pendingPurchaseDoc.pix.url);
      setQrcode(pendingPurchaseDoc.pix.qrcode);
      setExpirationTime(pendingPurchaseDoc.createdAt + 30 * 60 * 1000);
      setPixExpired(false);
      setIsPending(false);
    }

    if (pendingPurchaseDoc.status === "approved") {
      if (pendingPurchaseDoc.paymentMethod === "credit_card") {
        clearLocalCardAttemptFailures(visitorId);
      }

      const funnel = pendingPurchaseDoc.funnel;
      if (funnel?.upsellToken && funnel?.firstStepId) {
        const base =
          funnel.firstStepUrl && /^https?:\/\//i.test(funnel.firstStepUrl.trim())
            ? funnel.firstStepUrl.trim()
            : `${window.location.origin}/funnel-continue`;
        const u = new URL(base);
        u.searchParams.set("pid", pendingPurchaseDoc.id);
        u.searchParams.set("ft", funnel.upsellToken);
        u.searchParams.set("fstep", funnel.firstStepId);
        window.location.href = u.href;
      } else if (offer.tyPage) {
        window.location.href = offer.tyPage;
      } else {
        const thankYouToken = pendingPurchaseDoc.receiptToken;
        if (thankYouToken) {
          window.location.href = `${window.location.origin}/obrigado?pid=${pendingPurchaseDoc.id}&t=${thankYouToken}`;
        } else {
          window.location.href = `${window.location.origin}/obrigado`;
        }
      }
    } else if (pendingPurchaseDoc.status === "trial_started") {
      const funnel = pendingPurchaseDoc.funnel;
      if (funnel?.upsellToken && funnel?.firstStepId) {
        const base =
          funnel.firstStepUrl && /^https?:\/\//i.test(funnel.firstStepUrl.trim())
            ? funnel.firstStepUrl.trim()
            : `${window.location.origin}/funnel-continue`;
        const u = new URL(base);
        u.searchParams.set("pid", pendingPurchaseDoc.id);
        u.searchParams.set("ft", funnel.upsellToken);
        u.searchParams.set("fstep", funnel.firstStepId);
        window.location.href = u.href;
      } else if (offer.tyPage) {
        window.location.href = offer.tyPage;
      } else {
        const thankYouToken = pendingPurchaseDoc.receiptToken;
        if (thankYouToken) {
          window.location.href = `${window.location.origin}/obrigado?pid=${pendingPurchaseDoc.id}&t=${thankYouToken}`;
        } else {
          window.location.href = `${window.location.origin}/obrigado`;
        }
      }
    } else if (pendingPurchaseDoc.status === "declined") {
      if (pendingPurchaseDoc.paymentMethod === "credit_card") {
        recordBackendCardFailure(pendingPurchaseDoc.id);
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        card: {
          error_type: "Transação recusada",
          error_message: pendingPurchaseDoc.status_description,
        },
      }));
      setIsPending(false);
    } else if (pendingPurchaseDoc.status === "failed") {
      if (pendingPurchaseDoc.paymentMethod === "credit_card") {
        recordBackendCardFailure(pendingPurchaseDoc.id);
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        card: {
          error_type: "Transação recusada",
          error_message: "Erro ao processar pagamento",
        },
      }));
      setIsPending(false);
    } else if (pendingPurchaseDoc.status === "trial_setup_error") {
      const statusReason =
        pendingPurchaseDoc.status_reason ||
        pendingPurchaseDoc.status_description;
      let errorMessage =
        "Não foi possível ativar o período de teste. Entre em contato com o suporte";

      // Provide more specific error messages based on status_reason
      if (statusReason === "card_error") {
        errorMessage =
          "Falha na verificação do cartão. Verifique os dados ou tente outro cartão";
      } else if (statusReason === "customer_error") {
        errorMessage =
          "Erro ao processar os dados do cliente. Verifique as informações e tente novamente";
      } else if (statusReason === "subscription_error") {
        errorMessage =
          "Erro ao criar a assinatura do período de teste. Entre em contato com o suporte";
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        card: {
          error_type: "Erro no período de teste",
          error_message: errorMessage,
        },
      }));
      setIsPending(false);
    }
  }, [offer, pendingPurchaseDoc, visitorId]);

  useEffect(() => {
    if (!purchaseResult) return;

    if (purchaseResult.status === "initiated") {
      setPendingPurchaseId(purchaseResult.purchaseId);
    } else if (purchaseResult.status === "error") {
      setIsPending(false);
      setPendingPurchaseId("error");

      // Handle specific error types
      if (purchaseResult.error === "too_many_payment_attempts") {
        recordBackendCardFailure(
          `blocked:${purchaseResult.blockedUntil || Date.now()}`
        );

        setErrors((prevErrors) => ({
          ...prevErrors,
          card: {
            error_type: "Too many payment attempts",
            error_message: purchaseResult.message || CARD_ATTEMPT_BLOCK_MESSAGE,
          },
        }));
      } else if (purchaseResult.error === "error_creating_card") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          card: {
            error_type: "Erro no cartão de crédito",
            error_message:
              "Não foi possível processar os dados do cartão. Verifique as informações e tente novamente",
          },
        }));
      } else if (purchaseResult.error === "invalid_customer_email") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          email:
            purchaseResult.message ||
            "Não foi possível validar este e-mail. Use outro endereço.",
        }));
      } else if (purchaseResult.error === "duplicate_trial_not_allowed") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          card: {
            error_type: "Período de teste já utilizado",
            error_message:
              purchaseResult.message ||
              "Não é possível ativar mais de um período de teste para a mesma oferta",
          },
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          purchase: {
            error_type: "Erro ao iniciar a compra",
            error_message: purchaseResult.message || "erro desconhecido",
          },
        }));
      }
    } else {
      setIsPending(false);
      setPendingPurchaseId("error");
      setErrors((prevErrors) => ({
        ...prevErrors,
        purchase: {
          error_type: "Erro ao iniciar a compra",
          error_message: "erro desconhecido",
        },
      }));
    }
  }, [purchaseResult, visitorId]);

  /* useEffect(() => {
    if (apiDoc && apiDoc.url && apiDoc.url !== apiUrl) {
      setApiUrl(apiDoc.url);
    }
  }, [apiDoc]);

  useEffect(() => {
    if (altApiDoc && altApiDoc.url && altApiDoc.url !== apiUrl) {
      setApiUrl(altApiDoc.url);
    }
  }, [altApiDoc]); */

  // Cookie storage utilities — scoped to current hostname to avoid leaking
  // buyer tracking data (fbclid, UTMs) to unrelated subdomains like app.atomicpay.com.br.
  const CookieStorage = {
    set: (name, value, maxAgeSeconds = 30 * 24 * 60 * 60) => {
      const cookieValue = encodeURIComponent(JSON.stringify(value));
      // No explicit domain: browser defaults to exact current hostname (checkout / pay subdomain).
      document.cookie = `${name}=${cookieValue}; path=/; max-age=${maxAgeSeconds}; secure; samesite=lax`;
    },

    get: (name) => {
      const cookies = document.cookie.split(";");
      for (let cookie of cookies) {
        const [cookieName, cookieValue] = cookie.trim().split("=");
        if (cookieName === name) {
          try {
            return JSON.parse(decodeURIComponent(cookieValue));
          } catch (e) {
            console.error("Failed to parse cookie:", e);
            return null;
          }
        }
      }
      return null;
    },

    remove: (name) => {
      document.cookie = `${name}=; path=/; max-age=0`;
    },
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const newOrigin = {};
    const timestamp = new Date().getTime();

    // Verifica UTMs individualmente e só adiciona se existirem
    const newUtm = {};
    const utmSource = queryParams.get("utm_source");
    const utmMedium = queryParams.get("utm_medium");
    const utmCampaign = queryParams.get("utm_campaign");
    const utmContent = queryParams.get("utm_content");
    const utmTerm = queryParams.get("utm_term");
    const fbclid = queryParams.get("fbclid");

    // Se tem fbclid mas não tem utm_source, adiciona utm_source=facebook
    if (fbclid && !utmSource) {
      newUtm.source = "facebook";
    } else if (utmSource) {
      newUtm.source = utmSource;
    }

    if (utmMedium) newUtm.medium = utmMedium;
    if (utmCampaign) newUtm.campaign = utmCampaign;
    if (utmContent) newUtm.content = utmContent;
    if (utmTerm) newUtm.term = utmTerm;

    // Só adiciona o objeto `utm` a `newOrigin` se tiver ao menos 1 propriedade
    if (Object.keys(newUtm).length > 0) {
      newOrigin.utm = newUtm;
    }

    // Rastreia fbclid separadamente para debugging
    if (fbclid) newOrigin.fbclid = fbclid;

    // Verifica xcode, src, sck, sa
    const xcode = queryParams.get("xcode");
    const src = queryParams.get("src");
    const sck = queryParams.get("sck");
    const sa = queryParams.get("sa");

    if (xcode) newOrigin.xcode = xcode;
    if (src) newOrigin.src = src;
    if (sck) newOrigin.sck = sck;
    if (sa) newOrigin.sa = sa;

    setOrigin(newOrigin);

    // Handle originJourney tracking with cookies for cross-subdomain support
    const journeyKey = `user_journey`; // Single journey for the entire user

    // Check for compressed journey in URL (from external domains)
    const compressedJourney = queryParams.get("_j");
    let externalJourney = null;
    if (compressedJourney) {
      try {
        // Decode base64 URL-safe with Unicode support
        const base64 = compressedJourney.replace(/-/g, "+").replace(/_/g, "/");
        const binString = atob(base64);
        const bytes = Uint8Array.from(binString, (m) => m.codePointAt(0));
        const json = new TextDecoder().decode(bytes);
        const simplified = JSON.parse(json);
        // Reconstruct full journey from simplified version
        externalJourney = {
          attribution: simplified.a,
          history:
            simplified.h?.map((h) => ({
              utm: h.u,
              src: h.s,
              sck: h.k,
              xcode: h.x,
              sa: h.sa,
              timestamp: h.t,
              url: h.url,
              type: h.sa ? "sales_interaction" : "campaign",
              source: h.src || "external",
            })) || [],
          external: true, // Mark as coming from external domain
        };
      } catch (e) {
        console.error("Failed to decompress journey:", e);
      }
    }

    // Always process journey tracking (even without parameters)
    // Get existing journey from cookie
    let journey = CookieStorage.get(journeyKey);

    // Handle external journey if present
    if (externalJourney) {
      if (!journey) {
        journey = externalJourney;
      } else {
        // Merge external journey history with local journey
        journey.history = [
          ...(externalJourney.history || []),
          ...(journey.history || []),
        ];
        // Keep only last 10 entries
        if (journey.history.length > 10) {
          journey.history = journey.history.slice(-10);
        }
        // Use external attribution if local doesn't have one
        if (!journey.attribution && externalJourney.attribution) {
          journey.attribution = externalJourney.attribution;
        }
      }
    }

    // Always create a history entry for current visit (even without params)
    const historyEntry = {
      ...newOrigin,
      timestamp,
      type: sa ? "sales_interaction" : "campaign",
      source: window.location.hostname, // Always track subdomain
      url: window.location.href.split("?")[0], // Always track URL without params
    };

    if (!journey) {
      // First visit - initialize journey
      const hasParams = Object.keys(newOrigin).length > 0;
      journey = {
        attribution: hasParams
          ? { ...newOrigin, timestamp }
          : { timestamp, source: window.location.hostname },
        history: [historyEntry],
        createdAt: timestamp,
        lastUpdated: timestamp,
      };
    } else {
      // Update existing journey
      journey.lastUpdated = timestamp;
      journey.history = journey.history || [];

      // Check if this is a different interaction (avoid duplicates)
      const lastEntry = journey.history[journey.history.length - 1];

      // Helper function to check if two UTM objects are different
      const areUtmsDifferent = (utm1, utm2) => {
        if (!utm1 && !utm2) return false;
        if (!utm1 || !utm2) return true;

        const keys1 = Object.keys(utm1).sort();
        const keys2 = Object.keys(utm2).sort();
        if (keys1.length !== keys2.length) return true;

        return keys1.some((key) => utm1[key] !== utm2[key]);
      };

      // Check if any tracked parameter has changed OR if URL/source changed
      const isDifferent =
        !lastEntry ||
        areUtmsDifferent(lastEntry.utm, newOrigin.utm) ||
        lastEntry.sa !== newOrigin.sa ||
        lastEntry.src !== newOrigin.src ||
        lastEntry.sck !== newOrigin.sck ||
        lastEntry.xcode !== newOrigin.xcode ||
        lastEntry.source !== historyEntry.source ||
        lastEntry.url !== historyEntry.url;

      // Also check if enough time has passed (e.g., 30 seconds) to consider it a new visit
      const timeDifference = lastEntry
        ? timestamp - lastEntry.timestamp
        : Infinity;
      const isNewVisit = timeDifference > 30000; // 30 seconds

      if (isDifferent || isNewVisit) {
        journey.history.push(historyEntry);

        // Limit history to last 10 entries to avoid cookie size issues
        if (journey.history.length > 10) {
          journey.history = journey.history.slice(-10);
        }
      }
    }

    // Save updated journey to cookie
    CookieStorage.set(journeyKey, journey);
    setOriginJourney(journey);
  }, [checkoutUrlCode]);

  // ------ useEffects end ------

  const getCheckoutTitle = () => {
    let title = "";
    if (product) {
      title = product.name;
    }

    if (plan?.name) {
      title += " | " + plan.name;
    }

    return title;
  };

  const paymentMethodsHaystack = () => {
    const pm = offer?.paymentMethods;
    if (pm == null) return "";
    if (typeof pm === "string") return pm.toLowerCase();
    if (Array.isArray(pm)) return pm.join(" ").toLowerCase();
    return String(pm).toLowerCase();
  };

  const showDisplayCreditCard = () => {
    const h = paymentMethodsHaystack();
    return (
      h.includes("cartão") ||
      h.includes("cartao") ||
      h.includes("credit") ||
      h.includes("todos")
    );
  };

  const showDisplayPix = () => {
    if (isStripeCheckout) return false;
    const h = paymentMethodsHaystack();
    return h.includes("pix") || h.includes("todos");
  };

  const getPurchaseButtonText = () => {
    const hasTrial = offer?.trial > 0;

    if (paymentMethod === "credit_card") {
      if (pending) {
        switch (fetchCount) {
          case 0:
            return "Processando pagamento...";
          case 1:
            return "Pagamento a caminho! Aguarde um instante...";
          case 2:
            return "Verificando status do pagamento...";
          default:
            return "Processando pagamento...";
        }
      } else {
        return hasTrial
          ? `Iniciar teste grátis de ${offer.trial} dias`
          : "Pagar agora";
      }
    } else {
      if (pending) {
        if (fetchCount === 0) {
          return "Gerando pix...";
        } else if (fetchCount === 1) {
          return "O pix está quase pronto...";
        } else {
          return "Quase lá! Aguarde um instante...";
        }
      } else {
        return hasTrial
          ? `Gerar pix para teste grátis de ${offer.trial} dias`
          : "Gerar pix";
      }
    }
  };

  const pending =
    isPending ||
    pendingPurchaseDoc?.status === "initiated" ||
    pendingPurchaseDoc?.status === "trial_initiated" ||
    (pendingPurchaseDoc?.status === "pix_generated" && paymentMethod === "pix");

  // Extract pageSettings and theme early with fallback
  let pageSettings = {};

  if (isMobile) {
    const mobilePageSettings = checkoutDesign?.mobilePageSettings;
    if (mobilePageSettings && Object.keys(mobilePageSettings).length > 0) {
      pageSettings = mobilePageSettings;
    } else {
      // Fall back to desktop page settings if mobile settings don't exist
      pageSettings = checkoutDesign?.desktopPageSettings || {};
    }
  } else {
    pageSettings = checkoutDesign?.desktopPageSettings || {};
  }

  // Extract all theme properties
  const primaryColor =
    checkoutDesign?.theme?.primaryColor || "rgb(29, 79, 215)";
  const qrCodeColor =
    checkoutDesign?.theme?.qrCodeColor ||
    checkoutDesign?.theme?.primaryColor ||
    "rgb(29, 79, 215)";
  const primaryButton =
    checkoutDesign?.theme?.primaryButton ||
    checkoutDesign?.theme?.primaryColor ||
    "#A7E600";

  const textColor = checkoutDesign?.theme?.textColor || "#374151";
  const secondaryColor = checkoutDesign?.theme?.secondaryColor || "#000000";
  const mutedColor = checkoutDesign?.theme?.mutedColor || "#6B7280";
  const inputGlow =
    checkoutDesign?.theme?.inputGlow !== undefined
      ? checkoutDesign?.theme?.inputGlow
      : true;

  // Helper function to lighten color for hover state
  const lightenColor = (hex, percent = 20) => {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  };

  // Function to determine if a color is dark or light
  const isColorDark = (color) => {
    if (!color) return false;

    // Convert color to RGB
    let r, g, b;

    // Handle hex colors
    if (color.startsWith("#")) {
      const hex = color.replace("#", "");
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }
    }
    // Handle rgb/rgba colors
    else if (color.startsWith("rgb")) {
      const values = color.match(/\d+/g);
      if (values) {
        r = parseInt(values[0]);
        g = parseInt(values[1]);
        b = parseInt(values[2]);
      }
    }
    // Default to light if we can't parse
    else {
      return false;
    }

    // Calculate luminance using the relative luminance formula
    // https://www.w3.org/TR/WCAG20/#relativeluminancedef
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return true if dark (luminance < 0.5), false if light
    return luminance < 0.5;
  };

  const primaryHover = lightenColor(primaryColor, 5);
  const primaryButtonHover = lightenColor(primaryButton, 5);

  // Determine which logo to use based on checkout background color
  const checkoutBackgroundColor =
    checkoutDesign?.theme?.checkoutBackground ||
    pageSettings.bgColor ||
    "#ffffff";
  const isDarkBackground = isColorDark(checkoutBackgroundColor);
  const atomicPayLogoUrl = isDarkBackground
    ? "https://assets.atomicpay.net/public/all-white-logo.png"
    : "https://assets.atomicpay.net/public/all-black-logo.png";

  // Default theme configuration with derived colors
  const theme = {
    // Primary colors
    primaryColor,
    primaryHover,
    primaryGradientStart: checkoutDesign?.theme?.primaryGradientStart || null,
    primaryGradientEnd: checkoutDesign?.theme?.primaryGradientEnd || null,

    // Form styling
    inputBackground: checkoutDesign?.theme?.inputBackground || "transparent",
    inputBorder: checkoutDesign?.theme?.inputBorder || "#E5E7EB",
    inputText: checkoutDesign?.theme?.inputText || "#000000",
    inputPlaceholder: mutedColor,
    inputFocusBorder: primaryColor,

    // Button styling
    primaryButton: primaryButton,
    primaryButtonHover: primaryButtonHover,
    primaryButtonText: secondaryColor,

    // Text colors
    headingText: textColor, // Usar cor primária para headings
    bodyText: textColor,
    mutedText: mutedColor,

    // Error states
    errorText: checkoutDesign?.theme?.errorText || "#EF4444",
    errorBackground: checkoutDesign?.theme?.errorBackground || "#FEE2E2",

    // Success states
    successText: checkoutDesign?.theme?.successText || "#64a30d",
    successBackground:
      checkoutDesign?.theme?.successBackground || `${primaryColor}10`,

    // General
    cardBackground: checkoutDesign?.theme?.cardBackground || "#FFFFFF",
    cardBorder: checkoutDesign?.theme?.cardBorder || "#E5E7EB",
  };

  // Prepare page styles
  const pageStyles = (() => {
    const bgMode =
      pageSettings.bgMode ||
      (pageSettings.bgColor && pageSettings.bgColor !== "transparent"
        ? "solid"
        : "none");

    const baseStyle = {
      color: theme.textColor || "#000000",
      minHeight: "100vh",
    };

    if (bgMode === "none") {
      baseStyle.backgroundColor = "transparent";
    } else if (bgMode === "solid") {
      baseStyle.backgroundColor =
        pageSettings.bgColor || theme.backgroundColor || "#f1f1f1";
    } else if (bgMode === "gradient") {
      const start = pageSettings.gradientStartColor || "#FFFFFF";
      const end = pageSettings.gradientEndColor || "#F3F4F6";
      baseStyle.backgroundImage = `linear-gradient(135deg, ${start}, ${end})`;
    } else if (bgMode === "image" && pageSettings.bgImageUrl) {
      baseStyle.backgroundImage = `url(${mediaUrl(pageSettings.bgImageUrl)})`;
      baseStyle.backgroundRepeat = pageSettings.bgRepeat
        ? "repeat"
        : "no-repeat";
      baseStyle.backgroundAttachment = pageSettings.bgFixed
        ? "fixed"
        : undefined;
      baseStyle.backgroundSize = pageSettings.bgCover ? "cover" : undefined;
      baseStyle.backgroundPosition = "center";
      baseStyle.backgroundColor = pageSettings.bgColor || "#f1f1f1";
    }

    return baseStyle;
  })();

  // Apply pageBackground to body element
  useEffect(() => {
    if (embedCheckout) {
      const previousBody = document.body.style.background;
      const previousRoot = document.documentElement.style.background;
      document.body.style.background = "transparent";
      document.documentElement.style.background = "transparent";
      return () => {
        document.body.style.background = previousBody;
        document.documentElement.style.background = previousRoot;
      };
    }
    // Save original body background styles
    const originalStyles = {
      backgroundColor: document.body.style.backgroundColor,
      backgroundImage: document.body.style.backgroundImage,
      backgroundRepeat: document.body.style.backgroundRepeat,
      backgroundAttachment: document.body.style.backgroundAttachment,
      backgroundSize: document.body.style.backgroundSize,
      backgroundPosition: document.body.style.backgroundPosition,
    };

    // Determine background mode
    const bgMode =
      pageSettings.bgMode ||
      (pageSettings.bgColor && pageSettings.bgColor !== "transparent"
        ? "solid"
        : "none");

    // Apply background based on mode
    if (bgMode === "none") {
      document.body.style.backgroundColor = "transparent";
      document.body.style.backgroundImage = "none";
    } else if (bgMode === "solid") {
      document.body.style.backgroundColor =
        pageSettings.bgColor || theme.backgroundColor || "#f1f1f1";
      document.body.style.backgroundImage = "none";
    } else if (bgMode === "gradient") {
      const start = pageSettings.gradientStartColor || "#FFFFFF";
      const end = pageSettings.gradientEndColor || "#F3F4F6";
      document.body.style.backgroundImage = `linear-gradient(135deg, ${start}, ${end})`;
      document.body.style.backgroundColor = "transparent";
    } else if (bgMode === "image" && pageSettings.bgImageUrl) {
      document.body.style.backgroundImage = `url(${mediaUrl(pageSettings.bgImageUrl)})`;
      document.body.style.backgroundRepeat = pageSettings.bgRepeat
        ? "repeat"
        : "no-repeat";
      document.body.style.backgroundAttachment = pageSettings.bgFixed
        ? "fixed"
        : "scroll";
      document.body.style.backgroundSize = pageSettings.bgCover
        ? "cover"
        : "auto";
      document.body.style.backgroundPosition = "center";
      document.body.style.backgroundColor = pageSettings.bgColor || "#f1f1f1";
    }

    // Cleanup on unmount
    return () => {
      Object.keys(originalStyles).forEach((key) => {
        document.body.style[key] = originalStyles[key];
      });
    };
  }, [embedCheckout, pageSettings, theme.backgroundColor]);

  useEffect(() => {
    if (!embedCheckout) return undefined;
    const post = () => {
      const root = document.getElementById("checkout-main-container") || document.body;
      const height = Math.ceil(root.getBoundingClientRect().height);
      if (height < 200) return;
      window.parent?.postMessage(
        { source: "atomicpay-checkout-embed", type: "height", height },
        "*"
      );
    };
    post();
    const root = document.getElementById("checkout-main-container");
    const observer = new ResizeObserver(post);
    if (root) observer.observe(root);
    const timer = window.setInterval(post, 1000);
    return () => {
      observer.disconnect();
      window.clearInterval(timer);
    };
  }, [embedCheckout, offer, product]);

  // Create input styles based on theme
  const inputStyles = {
    backgroundColor:
      theme.inputBackground === "transparent"
        ? "transparent"
        : theme.inputBackground,
    borderColor: theme.inputBorder,
    color: theme.inputText,
    "--placeholder-color": theme.inputPlaceholder,
  };

  const inputClassName = `h-12 themed-input ${inputGlow ? "input-glow" : ""}`;

  // Calculate if there are side components
  const hasSideComponents = (() => {
    if (embedCheckout || isMobile) return false;

    let components = [];
    if (isMobile) {
      const mobileComponents = checkoutDesign?.mobileComponents;
      if (mobileComponents && Object.keys(mobileComponents).length > 0) {
        components = Object.values(mobileComponents);
      } else {
        components = Object.values(checkoutDesign?.desktopComponents || {});
      }
    } else {
      components = Object.values(checkoutDesign?.desktopComponents || {});
    }

    const sideComponents = components.filter(
      (c) =>
        !c.parentId &&
        c.area === "side" &&
        !(c.type === "timer" && c.content?.fixed === true)
    );

    return sideComponents.length > 0;
  })();
  const hasTopComponents = (() => {
    if (embedCheckout) return false;
    if (isMobile) return false;
    let components = [];
    if (isMobile) {
      components = Object.values(checkoutDesign?.mobileComponents || {});
    }
  })();

  if (!offer.code) {
    return <Loading />;
  }

  if (
    availableForPurchase.what === "plan" &&
    availableForPurchase.status !== "active" &&
    availableForPurchase.status !== "loading"
  ) {
    return <InactivePlan />;
  }

  if (
    availableForPurchase.what === "product" &&
    availableForPurchase.status !== "active" &&
    availableForPurchase.status !== "loading"
  ) {
    return <InactiveProduct />;
  }

  if (exceededFetchCount) {
    return <ErrorPage />;
  }

  if (errors.checkoutUrl === "not-found") {
    return (
      <ErrorPage
        title="Ocorreu um erro ao carregar o checkout."
        description="Tente novamente mais tarde ou entre em contato com o suporte."
        errorCode="checkout-url-not-found"
      />
    );
  }

  if (upgradeLink?.used) {
    return (
      <ErrorPage
        title="Link de upgrade já utilizado"
        description="O link de upgrade já foi utilizado. <br /> Se você acredita que isso é um erro, entre em contato com o suporte."
      />
    );
  }

  return (
    <div
      className={
        embedCheckout
          ? "w-full"
          : "p-2.5 sm:p-5 w-full flex justify-center"
      }
      style={embedCheckout ? { background: "transparent" } : pageStyles}
    >
      {/* Dynamic theme styles */}
      <style>
        {`
          .themed-input::placeholder {
            color: ${theme.inputPlaceholder};
            opacity: 1;
          }
          
          /* Input glow effect */
          .input-glow:focus {
            border-color: ${theme.inputFocusBorder} !important;
            box-shadow: 0 0 0 3px ${theme.inputFocusBorder}20 !important;
            outline: none !important;
          }
          
          /* No glow - just border color change */
          .themed-input:not(.input-glow):focus {
            border-color: ${theme.inputFocusBorder} !important;
            outline: none !important;
          }
          
          .themed-select-container:focus-within {
            border-color: ${theme.inputFocusBorder} !important;
            box-shadow: ${
              inputGlow ? `0 0 0 3px ${theme.inputFocusBorder}20` : "none"
            } !important;
          }
          
          /* Gradient border for payment buttons using mask technique */
          .payment-button {
            position: relative;
            overflow: hidden;
            transition: all 0.15s ease !important;
          }
          
          .payment-button.gradient-border::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border-radius: 0.5rem;
            padding: 1px;
            background: linear-gradient(135deg, ${
              theme.primaryGradientStart || primaryColor
            }, ${theme.primaryGradientEnd || primaryColor});
            -webkit-mask: 
              linear-gradient(#fff 0 0) content-box, 
              linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            pointer-events: none;
          }
          
          /* Rich content styles for RTE */
          .rich-content p {
            margin-bottom: 0.5rem;
          }
          .rich-content p:last-child {
            margin-bottom: 0;
          }
          .rich-content strong,
          .rich-content b {
            font-weight: 700;
          }
          .rich-content em,
          .rich-content i {
            font-style: italic;
          }
          .rich-content u {
            text-decoration: underline;
          }
          .rich-content s,
          .rich-content strike {
            text-decoration: line-through;
          }
          .rich-content h1 {
            font-size: 2em;
            font-weight: 700;
            margin-bottom: 0.75rem;
          }
          .rich-content h2 {
            font-size: 1.5em;
            font-weight: 700;
            margin-bottom: 0.5rem;
          }
          .rich-content h3 {
            font-size: 1.25em;
            font-weight: 600;
            margin-bottom: 0.5rem;
          }
          .rich-content h4 {
            font-size: 1.125em;
            font-weight: 600;
            margin-bottom: 0.5rem;
          }
          .rich-content ul,
          .rich-content ol {
            margin-left: 1.5rem;
            margin-bottom: 0.5rem;
          }
          .rich-content ul {
            list-style-type: disc;
          }
          .rich-content ol {
            list-style-type: decimal;
          }
          .rich-content li {
            margin-bottom: 0.25rem;
          }
          .rich-content a {
            color: ${theme.primaryColor};
            text-decoration: underline;
          }
          .rich-content a:hover {
            opacity: 0.8;
          }
          .rich-content blockquote {
            border-left: 4px solid ${theme.primaryColor};
            padding-left: 1rem;
            margin-left: 0;
            margin-bottom: 0.5rem;
            font-style: italic;
            color: ${theme.mutedText};
          }
          .rich-content code {
            background-color: rgba(0, 0, 0, 0.05);
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            font-family: monospace;
            font-size: 0.875em;
          }
          .rich-content pre {
            background-color: rgba(0, 0, 0, 0.05);
            padding: 1rem;
            border-radius: 0.5rem;
            overflow-x: auto;
            margin-bottom: 0.5rem;
          }
          .rich-content pre code {
            background-color: transparent;
            padding: 0;
          }
          .rich-content hr {
            border: none;
            border-top: 1px solid ${theme.inputBorder};
            margin: 1rem 0;
          }
          .rich-content table {
            border-collapse: collapse;
            width: 100%;
            margin-bottom: 0.5rem;
          }
          .rich-content th,
          .rich-content td {
            border: 1px solid ${theme.inputBorder};
            padding: 0.5rem;
            text-align: left;
          }
          .rich-content th {
            background-color: rgba(0, 0, 0, 0.05);
            font-weight: 600;
          }
          .rich-content img {
            max-width: 100%;
            height: auto;
          }
          .rich-content .text-left {
            text-align: left;
          }
          .rich-content .text-center {
            text-align: center;
          }
          .rich-content .text-right {
            text-align: right;
          }
          .rich-content .text-justify {
            text-align: justify;
          }
        `}
      </style>

      <div
        id="checkout-main-container"
        className={cn(
          embedCheckout
            ? "w-full"
            : "w-full md:w-2/3 lg:w-1/2 xl:max-w-3xl rounded-lg",
          !embedCheckout && (hasSideComponents || hasTopComponents)
            ? "w-full lg:w-[1024px] xl:max-w-full pb-5 px-12"
            : embedCheckout
            ? ""
            : "sm:px-3"
        )}
        style={{
          backgroundColor: embedCheckout
            ? "transparent"
            : checkoutDesign?.theme?.checkoutBackground ||
              pageSettings.bgColor ||
              theme.backgroundColor ||
              "#ffffff",
        }}
      >
        {/* Extract components by area */}
        {(() => {
          // Try to get mobile components first, fall back to desktop if not available
          let components = [];

          if (isMobile) {
            const mobileComponents = checkoutDesign?.mobileComponents;
            if (mobileComponents && Object.keys(mobileComponents).length > 0) {
              components = Object.values(mobileComponents);
            } else {
              // Fall back to desktop components if mobile components don't exist
              components = Object.values(
                checkoutDesign?.desktopComponents || {}
              );
            }
          } else {
            components = Object.values(checkoutDesign?.desktopComponents || {});
          }

          // Separate fixed components from regular components
          const fixedComponents = components.filter(
            (c) =>
              !c.parentId && c.type === "timer" && c.content?.fixed === true
          );

          const topComponents = components.filter(
            (c) =>
              !c.parentId &&
              c.area === "top" &&
              !(c.type === "timer" && c.content?.fixed === true)
          );
          const sideComponents = components.filter(
            (c) =>
              !c.parentId &&
              c.area === "side" &&
              !(c.type === "timer" && c.content?.fixed === true)
          );
          const bottomComponents = components.filter(
            (c) =>
              !c.parentId &&
              c.area === "bottom" &&
              !(c.type === "timer" && c.content?.fixed === true)
          );

          // Check if there's a custom header component
          const hasCustomHeader = components.some(
            (c) =>
              c.type === "header" ||
              (c.type === "container" &&
                c.label?.toLowerCase().includes("header"))
          );

          const hasCustomDesign =
            topComponents.length > 0 ||
            sideComponents.length > 0 ||
            bottomComponents.length > 0;
          const hasSideComponents =
            !embedCheckout && sideComponents.length > 0 && !isMobile;
          const hasTopComponents = !embedCheckout && topComponents.length > 0;

          return (
            <div
              id="checkout-page-container"
              style={{
                fontFamily: pageSettings.fontFamily
                  ? `'${pageSettings.fontFamily}', sans-serif`
                  : 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              {/* Render fixed components outside the main content flow */}
              {embedCheckout
                ? null
                : fixedComponents.map((component) => {
                const isHeader =
                  component.type === "header" ||
                  (component.type === "container" &&
                    component.label?.toLowerCase().includes("header"));

                return (
                  <div
                    key={component.id}
                    className={`fixed top-0 left-0 right-0 z-50${
                      isHeader ? " checkout-component-header" : ""
                    }`}
                  >
                    <ComponentRenderer
                      component={component}
                      allComponents={components}
                      isMobile={isMobile}
                      theme={theme}
                    />
                  </div>
                );
              })}

              {/* Add padding to main content if there are fixed components */}
              {/* Main content wrapper with consistent width */}
              <div
                className={cn(
                  hasSideComponents
                    ? "" // Full width when side components exist
                    : "flex flex-col items-center", // Center when no side components
                  fixedComponents.length > 0 && "pt-12" // Add padding when fixed components exist
                )}
              >
                {/* Render top area components */}
                {!embedCheckout && topComponents.length > 0 && (
                  <div
                    id="checkout-top-components"
                    className={cn(
                      "mb-3 w-full flex flex-col gap-3",
                      !hasSideComponents &&
                        "sm:px-5 md:w-2/3 lg:w-1/2 2xl:max-w-2xl",
                      hasTopComponents &&
                        "sm:pt-5 md:w-full lg:w-full 2xl:max-w-none"
                    )}
                  >
                    {topComponents.map((component) => {
                      const isHeader =
                        component.type === "header" ||
                        (component.type === "container" &&
                          component.label?.toLowerCase().includes("header"));

                      return isHeader ? (
                        <div
                          key={component.id}
                          className="checkout-component-header"
                        >
                          <ComponentRenderer
                            component={component}
                            allComponents={components}
                            isMobile={isMobile}
                            theme={theme}
                          />
                        </div>
                      ) : (
                        <ComponentRenderer
                          key={component.id}
                          component={component}
                          allComponents={components}
                          isMobile={isMobile}
                          theme={theme}
                        />
                      );
                    })}
                  </div>
                )}

                {/* Main content area with optional side components */}
                <div
                  className={cn(
                    "flex gap-5 w-full",
                    hasSideComponents ? "flex-row" : "justify-center"
                  )}
                >
                  {/* Main checkout form */}
                  <div
                    id="checkout-core"
                    style={{
                      backgroundColor:
                        checkoutDesign?.theme?.checkoutBackground ||
                        pageSettings.bgColor ||
                        theme.backgroundColor ||
                        "#ffffff",
                    }}
                    className={cn(
                      "w-full sm:px-5 rounded-lg",
                      hasSideComponents
                        ? "lg:flex-1 p-0 sm:p-0 sm:min-w-[612px]"
                        : hasTopComponents
                        ? "w-full"
                        : "w-full lg:max-w-2xl 2xl:max-w-2xl"
                    )}
                  >
                    {/* Only show default thumb if no custom TOP components */}
                    {!embedCheckout && topComponents.length === 0 && (
                      <>
                        {checkoutDesign?.thumbUrl && (
                          <img
                            src={checkoutDesign.thumbUrl.replace(import.meta.env.VITE_MEDIA_OLD_URL, import.meta.env.VITE_MEDIA_URL)}
                            alt=""
                            className="mb-5"
                          />
                        )}
                      </>
                    )}
                    {/* Only show default header if no custom header component */}
                    {!embedCheckout && !hasCustomHeader && !checkoutDesignCode && (
                      <>
                        <div
                          ref={logoRef}
                          id="checkout-header"
                          className="flex items-center gap-5 pt-2.5 mb-3"
                        >
                          {product.logo ? (
                            <img
                              className="w-20 h-20 rounded-sm"
                              src={product.logo.replace(import.meta.env.VITE_MEDIA_OLD_URL, import.meta.env.VITE_MEDIA_URL)}
                              alt=""
                            />
                          ) : null}
                          <div
                            className={`flex ${
                              product.logo
                                ? "flex-col"
                                : "flex-row justify-between text-right"
                            } sm:flex-row sm:justify-between sm:items-center w-full sm:pr-2.5`}
                          >
                            <div className="flex flex-col gap-1">
                              <div>
                                {product.name ? (
                                  <h1
                                    className="font-semibold text-lg sm:text-xl text-left"
                                    style={{ color: theme.headingText }}
                                  >
                                    {getCheckoutTitle()}
                                  </h1>
                                ) : (
                                  <Skeleton className="h-5 w-40" />
                                )}
                              </div>
                              <div>
                                {offer?.description ? (
                                  <p
                                    className="text-left text-xs sm:text-sm sm:leading-[20px]"
                                    style={{ color: theme.mutedText }}
                                  >
                                    {offer?.description || ""}
                                  </p>
                                ) : offer?.description === "" ? null : (
                                  <Skeleton className="mt-2.5 h-4 w-32" />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                    <>
                    <form id="checkout-form" className="flex flex-col gap-3">
                      <div>
                        <div
                          className={
                            upgradeLink?.customerEmail ? "relative" : ""
                          }
                        >
                          <Input
                            ref={emailRef}
                            type="email"
                            autoComplete="email"
                            placeholder="Digite seu e-mail para receber o acesso"
                            value={email}
                            onChange={handleEmailChange}
                            onBlur={(e) => handleEmailBlur(e.target.value)}
                            className={`${inputClassName} disabled:opacity-100`}
                            style={
                              upgradeLink?.customerEmail
                                ? {
                                    backgroundColor: hexToRgba(
                                      primaryColor,
                                      0.05
                                    ),
                                    borderColor: primaryColor,
                                    borderWidth: "1px",
                                    color: primaryColor,
                                  }
                                : inputStyles
                            }
                            disabled={qrcodeImage || upgradeLink?.customerEmail}
                            required
                          />
                          {upgradeLink?.customerEmail && (
                            <svg
                              className="absolute top-1/2 -translate-y-1/2 w-5 h-5"
                              style={{
                                left:
                                  upgradeLink?.customerEmail.length * 0.55 +
                                  "rem",
                                color:
                                  primaryColor === "#A7E600"
                                    ? "#64a30d"
                                    : primaryColor,
                              }}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          )}
                        </div>
                        {errors.email && (
                          <div className="mt-1.5 flex items-center gap-1">
                            <CrossCircledIcon
                              className="h-3 w-3"
                              style={{ color: theme.errorText }}
                            />
                            <span
                              className="text-xs"
                              style={{ color: theme.errorText }}
                            >
                              {errors.email}
                            </span>
                          </div>
                        )}
                      </div>
                      <div>
                        <Input
                          type="text"
                          autoComplete="name"
                          placeholder="Digite seu nome completo ou razão social"
                          value={name}
                          onChange={handleNameChange}
                          onBlur={(e) => handleNameBlur(e.target.value)}
                          className={inputClassName}
                          style={inputStyles}
                          disabled={qrcodeImage}
                          required
                        />
                        {errors.name && (
                          <div className="mt-1.5 flex items-center gap-1">
                            <CrossCircledIcon
                              className="h-3 w-3"
                              style={{ color: theme.errorText }}
                            />
                            <span
                              className="text-xs"
                              style={{ color: theme.errorText }}
                            >
                              {errors.name}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-3">
                        {!isStripeCheckout && <Input
                          maxLength={18}
                          type="text"
                          autoComplete="off"
                          placeholder="Digite seu CPF ou CNPJ"
                          value={taxId}
                          onChange={handleTaxIdChange}
                          onBlur={handleTaxIdBlur}
                          className={inputClassName}
                          style={inputStyles}
                          disabled={qrcodeImage}
                          required
                        />}
                        <Input
                          maxLength={15}
                          type="text"
                          autoComplete="tel"
                          placeholder="Digite seu celular"
                          value={phoneNumber}
                          onChange={handlePhoneNumberChange}
                          onBlur={(e) => handlePhoneNumberBlur(e.target.value)}
                          className={inputClassName}
                          style={inputStyles}
                          disabled={qrcodeImage}
                          required
                        />
                      </div>
                      {(errors.taxId || errors.phoneNumber) && (
                        <div className="flex gap-3">
                          {errors.taxId && (
                            <div className="flex items-center gap-1 w-1/2">
                              <CrossCircledIcon
                                className="h-3 w-3"
                                style={{ color: theme.errorText }}
                              />
                              <span
                                className="text-xs"
                                style={{ color: theme.errorText }}
                              >
                                {errors.taxId}
                              </span>
                            </div>
                          )}
                          {errors.phoneNumber && (
                            <>
                              {!errors.taxId && <div className="w-1/2"></div>}
                              <div className="flex items-center gap-1">
                                <CrossCircledIcon
                                  className="h-3 w-3"
                                  style={{ color: theme.errorText }}
                                />
                                <span
                                  className="text-xs"
                                  style={{ color: theme.errorText }}
                                >
                                  {errors.phoneNumber}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {upgradeLink?.upgrade && (
                        <div
                          className="mt-3 p-4 rounded-lg"
                          style={{
                            backgroundColor: theme.successBackground,
                            borderColor: theme.successText,
                            borderWidth: "1px",
                            borderStyle: "solid",
                          }}
                        >
                          <div className="flex items-center gap-3">
                            <svg
                              className="w-5 h-5 shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              style={{ color: theme.successText }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <div className="flex-1">
                              <p
                                className="text-sm font-medium"
                                style={{ color: theme.successText }}
                              >
                                Desconto de upgrade aplicado!
                              </p>
                              <p
                                className="text-xs mt-1"
                                style={{ color: theme.mutedText }}
                              >
                                Código:{" "}
                                <span className="font-medium">
                                  {upgradeLinkCode}
                                </span>{" "}
                                • Você está economizando{" "}
                                <span className="font-medium">
                                  {getDiscountPercentage()}%
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {offer?.trial > 0 && (
                        <div
                          className="mt-3 p-4 rounded-lg"
                          style={{
                            backgroundColor: `${theme.primaryButton}10`,
                            borderColor: theme.primaryButton,
                            borderWidth: "1px",
                            borderStyle: "solid",
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-4 h-4 shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                              style={{
                                color:
                                  theme.primaryButton === "#A7E600"
                                    ? "#64a30d"
                                    : theme.primaryButton,
                              }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            <div className="flex-1">
                              <p
                                className="text-sm"
                                style={{
                                  color:
                                    theme.primaryButton === "#A7E600"
                                      ? "#64a30d"
                                      : theme.primaryButton,
                                }}
                              >
                                <span className="font-medium">
                                  Período de teste:
                                </span>{" "}
                                Você não será cobrado hoje
                              </p>
                              <p
                                className="text-xs mt-1"
                                style={{ color: theme.mutedText }}
                              >
                                A cobrança de{" "}
                                {Number(installments) > 1
                                  ? `${installments}x de R$ ${formatPrice(
                                      getSelectedInstallmentsValue()
                                    )}`
                                  : `R$ ${formatPrice(
                                      getSelectedInstallmentsValue()
                                    )}`}{" "}
                                iniciará após {offer.trial} dias
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="w-full mt-3 flex gap-3">
                        {showDisplayCreditCard() && (
                          <Button
                            type="button"
                            disabled={!product.id}
                            variant="outline"
                            size="xl"
                            className={cn(
                              "payment-button gap-3 w-full transition-all",
                              paymentMethod === "credit_card" &&
                                theme.primaryGradientStart &&
                                theme.primaryGradientEnd
                                ? "gradient-border"
                                : ""
                            )}
                            style={{
                              backgroundColor:
                                paymentMethod === "credit_card" ||
                                hoveredButton === "credit_card"
                                  ? hexToRgba(primaryColor, 0.05)
                                  : "transparent",
                              borderWidth:
                                theme.primaryGradientStart &&
                                theme.primaryGradientEnd &&
                                paymentMethod === "credit_card"
                                  ? "0"
                                  : "1px",
                              borderStyle: "solid",
                              borderColor:
                                !theme.primaryGradientStart ||
                                !theme.primaryGradientEnd ||
                                paymentMethod !== "credit_card"
                                  ? paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card"
                                    ? primaryColor
                                    : theme.inputBorder
                                  : "transparent",
                              color:
                                paymentMethod === "credit_card" ||
                                hoveredButton === "credit_card"
                                  ? theme.primaryGradientStart || primaryColor
                                  : theme.mutedText,
                            }}
                            onMouseEnter={() => setHoveredButton("credit_card")}
                            onMouseLeave={() => setHoveredButton(null)}
                            onClick={() => {
                              setPaymentMethod("credit_card");
                              setErrors({});
                              setIsPending(false);
                              const purchaseResultObj = { ...purchaseResult };
                              setPurchaseResultBackup(purchaseResultObj);
                              setPurchaseResult(null);
                              const qrImage = qrcodeImage;
                              setQrcodeImageBackup(qrImage);
                              setQrcodeImage(undefined);
                              setQrcode(undefined);
                            }}
                          >
                            <svg
                              focusable="false"
                              role="img"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 576 512"
                              className="svg-inline--fa fa-credit-card fa-w-18 mr-2 w-4 h-4 sm:w-6 sm:h-6"
                            >
                              {(paymentMethod === "credit_card" ||
                                hoveredButton === "credit_card") &&
                                theme.primaryGradientStart &&
                                theme.primaryGradientEnd && (
                                  <defs>
                                    <linearGradient
                                      id={gradientIds.creditCard}
                                      x1="0%"
                                      y1="0%"
                                      x2="100%"
                                      y2="100%"
                                    >
                                      <stop
                                        offset="0%"
                                        stopColor={theme.primaryGradientStart}
                                      />
                                      <stop
                                        offset="100%"
                                        stopColor={theme.primaryGradientEnd}
                                      />
                                    </linearGradient>
                                  </defs>
                                )}
                              <path
                                className="card-path"
                                fill={
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? `url(#${gradientIds.creditCard})`
                                    : paymentMethod === "credit_card" ||
                                      hoveredButton === "credit_card"
                                    ? primaryColor
                                    : theme.mutedText
                                }
                                d="M527.9 32H48.1C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48.1 48h479.8c26.6 0 48.1-21.5 48.1-48V80c0-26.5-21.5-48-48.1-48zM54.1 80h467.8c3.3 0 6 2.7 6 6v42H48.1V86c0-3.3 2.7-6 6-6zm467.8 352H54.1c-3.3 0-6-2.7-6-6V256h479.8v170c0 3.3-2.7 6-6 6zM192 332v40c0 6.6-5.4 12-12 12h-72c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h72c6.6 0 12 5.4 12 12zm192 0v40c0 6.6-5.4 12-12 12H236c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h136c6.6 0 12 5.4 12 12z"
                              />
                            </svg>
                            <span
                              className="cc-text font-normal lg:hidden"
                              style={{
                                background:
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? `linear-gradient(135deg, ${theme.primaryGradientStart}, ${theme.primaryGradientEnd})`
                                    : undefined,
                                WebkitBackgroundClip:
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? "text"
                                    : undefined,
                                WebkitTextFillColor:
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? "transparent"
                                    : undefined,
                                color:
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  (!theme.primaryGradientStart ||
                                    !theme.primaryGradientEnd)
                                    ? primaryColor
                                    : theme.mutedText,
                              }}
                            >
                              Cartão
                            </span>
                            <span
                              className="cc-text font-medium hidden lg:block"
                              style={{
                                background:
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? `linear-gradient(135deg, ${theme.primaryGradientStart}, ${theme.primaryGradientEnd})`
                                    : undefined,
                                WebkitBackgroundClip:
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? "text"
                                    : undefined,
                                WebkitTextFillColor:
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? "transparent"
                                    : undefined,
                                color:
                                  (paymentMethod === "credit_card" ||
                                    hoveredButton === "credit_card") &&
                                  (!theme.primaryGradientStart ||
                                    !theme.primaryGradientEnd)
                                    ? primaryColor
                                    : theme.mutedText,
                              }}
                            >
                              Cartão de Crédito
                            </span>
                          </Button>
                        )}
                        {showDisplayPix() && (
                          <Button
                            type="button"
                            variant="outline"
                            size="xl"
                            className={cn(
                              "payment-button gap-3 w-full transition-all",
                              paymentMethod === "pix" &&
                                theme.primaryGradientStart &&
                                theme.primaryGradientEnd
                                ? "gradient-border"
                                : ""
                            )}
                            style={{
                              backgroundColor:
                                paymentMethod === "pix" ||
                                hoveredButton === "pix"
                                  ? hexToRgba(primaryColor, 0.05)
                                  : "transparent",
                              borderWidth:
                                theme.primaryGradientStart &&
                                theme.primaryGradientEnd &&
                                paymentMethod === "pix"
                                  ? "0"
                                  : "1px",
                              borderStyle: "solid",
                              borderColor:
                                !theme.primaryGradientStart ||
                                !theme.primaryGradientEnd ||
                                paymentMethod !== "pix"
                                  ? paymentMethod === "pix" ||
                                    hoveredButton === "pix"
                                    ? primaryColor
                                    : theme.inputBorder
                                  : "transparent",
                              color:
                                paymentMethod === "pix" ||
                                hoveredButton === "pix"
                                  ? theme.primaryGradientStart || primaryColor
                                  : theme.mutedText,
                            }}
                            onMouseEnter={() => setHoveredButton("pix")}
                            onMouseLeave={() => setHoveredButton(null)}
                            onClick={() => {
                              setPaymentMethod("pix");
                              setErrors({});
                              if (purchaseResultBackup) {
                                setPurchaseResult(purchaseResultBackup);
                                setQrcodeImage(qrcodeImageBackup);
                              }
                            }}
                          >
                            <svg
                              width="166"
                              height="166"
                              viewBox="0 0 166 166"
                              xmlns="http://www.w3.org/2000/svg"
                              className="mr-2 w-4 h-4 sm:w-6 sm:h-6"
                            >
                              {(paymentMethod === "pix" ||
                                hoveredButton === "pix") &&
                                theme.primaryGradientStart &&
                                theme.primaryGradientEnd && (
                                  <defs>
                                    <linearGradient
                                      id={gradientIds.pix}
                                      x1="0%"
                                      y1="0%"
                                      x2="100%"
                                      y2="100%"
                                    >
                                      <stop
                                        offset="0%"
                                        stopColor={theme.primaryGradientStart}
                                      />
                                      <stop
                                        offset="100%"
                                        stopColor={theme.primaryGradientEnd}
                                      />
                                    </linearGradient>
                                  </defs>
                                )}
                              <path
                                className="pix-path"
                                d="M128.886 126.429C122.402 126.429 116.304 123.904 111.719 119.321L86.9305 94.5328C85.1908 92.7877 82.1569 92.7931 80.4173 94.5328L55.5383 119.412C50.9529 123.994 44.8548 126.519 38.3704 126.519H33.4857L64.8802 157.914C74.6853 167.718 90.5823 167.718 100.387 157.914L131.873 126.429H128.886Z"
                                fill={
                                  (paymentMethod === "pix" ||
                                    hoveredButton === "pix") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? `url(#${gradientIds.pix})`
                                    : paymentMethod === "pix" ||
                                      hoveredButton === "pix"
                                    ? primaryColor
                                    : theme.mutedText
                                }
                              />
                              <path
                                className="pix-path"
                                d="M38.3705 38.7476C44.8549 38.7476 50.953 41.2731 55.5376 45.8562L80.4166 70.739C82.2083 72.5308 85.1342 72.5385 86.9306 70.7367L111.719 45.9464C116.304 41.3633 122.402 38.8385 128.887 38.8385H131.872L100.388 7.35388C90.5824 -2.45126 74.6854 -2.45126 64.8803 7.35388L33.4866 38.7476H38.3705Z"
                                fill={
                                  (paymentMethod === "pix" ||
                                    hoveredButton === "pix") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? `url(#${gradientIds.pix})`
                                    : paymentMethod === "pix" ||
                                      hoveredButton === "pix"
                                    ? primaryColor
                                    : theme.mutedText
                                }
                              />
                              <path
                                className="pix-path"
                                d="M157.914 64.8806L138.888 45.8541C138.469 46.022 138.015 46.1269 137.537 46.1269H128.887C124.414 46.1269 120.036 47.9404 116.876 51.1026L92.0874 75.8914C89.7686 78.2109 86.7207 79.3722 83.6752 79.3722C80.6273 79.3722 77.5818 78.2109 75.2622 75.8937L50.3809 51.0124C47.2204 47.8503 42.8433 46.0368 38.3705 46.0368H27.7337C27.2805 46.0368 26.8561 45.9295 26.4558 45.7787L7.35385 64.8806C-2.45128 74.685 -2.45128 90.582 7.35385 100.388L26.455 119.489C26.8553 119.338 27.2805 119.231 27.7337 119.231H38.3705C42.8433 119.231 47.2204 117.417 50.3809 114.255L75.2599 89.3763C79.7567 84.8834 87.5952 84.8811 92.0874 89.3787L116.876 114.164C120.036 117.327 124.414 119.141 128.887 119.141H137.537C138.016 119.141 138.469 119.245 138.888 119.413L157.914 100.388C167.719 90.582 167.719 74.685 157.914 64.8806Z"
                                fill={
                                  (paymentMethod === "pix" ||
                                    hoveredButton === "pix") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? `url(#${gradientIds.pix})`
                                    : paymentMethod === "pix" ||
                                      hoveredButton === "pix"
                                    ? primaryColor
                                    : theme.mutedText
                                }
                              />
                            </svg>
                            <span
                              className="pix-text font-normal"
                              style={{
                                background:
                                  (paymentMethod === "pix" ||
                                    hoveredButton === "pix") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? `linear-gradient(135deg, ${theme.primaryGradientStart}, ${theme.primaryGradientEnd})`
                                    : undefined,
                                WebkitBackgroundClip:
                                  (paymentMethod === "pix" ||
                                    hoveredButton === "pix") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? "text"
                                    : undefined,
                                WebkitTextFillColor:
                                  (paymentMethod === "pix" ||
                                    hoveredButton === "pix") &&
                                  theme.primaryGradientStart &&
                                  theme.primaryGradientEnd
                                    ? "transparent"
                                    : undefined,
                                color:
                                  (paymentMethod === "pix" ||
                                    hoveredButton === "pix") &&
                                  (!theme.primaryGradientStart ||
                                    !theme.primaryGradientEnd)
                                    ? primaryColor
                                    : theme.mutedText,
                              }}
                            >
                              Pix
                            </span>
                          </Button>
                        )}
                      </div>
                      {isStripeCheckout && (
                        <StripePaymentForm session={stripeSession} />
                      )}
                      {paymentMethod === "credit_card" && !isStripeCheckout && (
                        <div className="mt-3 flex flex-col gap-3">
                          <Input
                            maxLength={30}
                            type="text"
                            autoComplete="cc-name"
                            placeholder="Nome do Titular do Cartão"
                            value={creditCard.holderName}
                            onChange={handleHolderNameChange}
                            className={inputClassName}
                            style={inputStyles}
                            required
                          />
                          {errors.holderName && (
                            <div className="flex items-center gap-1">
                              <CrossCircledIcon
                                className="h-3 w-3"
                                style={{ color: theme.errorText }}
                              />
                              <span
                                className="text-xs"
                                style={{ color: theme.errorText }}
                              >
                                {errors.holderName}
                              </span>
                            </div>
                          )}
                          <Input
                            type="text"
                            maxLength={19}
                            autoComplete="cc-number"
                            placeholder="Número do Cartão de Crédito"
                            value={creditCard.number}
                            onChange={handleCreditCardNumberChange}
                            onBlur={handleCreditCardNumberBlur}
                            className={inputClassName}
                            style={inputStyles}
                            required
                          />
                          {errors.creditCardNumber && (
                            <div className="flex items-center gap-1">
                              <CrossCircledIcon
                                className="h-3 w-3"
                                style={{ color: theme.errorText }}
                              />
                              <span
                                className="text-xs"
                                style={{ color: theme.errorText }}
                              >
                                {errors.creditCardNumber}
                              </span>
                            </div>
                          )}
                          <div className="flex gap-3">
                            <Input
                              type="text"
                              maxLength={5}
                              autoComplete="cc-exp"
                              placeholder="MM/AA"
                              value={creditCard.expDate}
                              onChange={handleExpDateChange}
                              onBlur={handleExpDateBlur}
                              className={inputClassName}
                              style={inputStyles}
                              required
                            />
                            <Input
                              type="text"
                              readOnly
                              className="hidden"
                              value={creditCard.expDate.split("/")[0] || ""}
                            />
                            <Input
                              type="text"
                              readOnly
                              className="hidden"
                              value={creditCard.expDate.split("/")[1] || ""}
                            />
                            <Input
                              type="text"
                              autoComplete="cc-csc"
                              maxLength={cardType === "amex" ? 4 : 3}
                              placeholder="Cód. de Segurança"
                              value={creditCard.cvv}
                              onChange={handleCVVChange}
                              className={inputClassName}
                              style={inputStyles}
                              required
                            />
                          </div>
                          <div className="flex gap-3">
                            {errors.expDate && (
                              <div className="w-1/2 flex items-center gap-1">
                                <CrossCircledIcon
                                  className="h-3 w-3"
                                  style={{ color: theme.errorText }}
                                />
                                <span
                                  className="text-xs"
                                  style={{ color: theme.errorText }}
                                >
                                  {errors.expDate}
                                </span>
                              </div>
                            )}
                            {errors.cvv && (
                              <div className="w-1/2 flex items-center gap-1">
                                <CrossCircledIcon
                                  className="h-3 w-3"
                                  style={{ color: theme.errorText }}
                                />
                                <span
                                  className="text-xs"
                                  style={{ color: theme.errorText }}
                                >
                                  {errors.cvv}
                                </span>
                              </div>
                            )}
                          </div>
                          {availableInstallments.length > 1 && (
                            <div
                              className="flex relative flex-col h-[72px] w-full items-start justify-between rounded-md px-3 py-2 text-sm themed-select-container"
                              style={{
                                border: `1px solid ${theme.inputBorder}`,
                                backgroundColor:
                                  theme.inputBackground === "transparent"
                                    ? "transparent"
                                    : theme.inputBackground,
                              }}
                            >
                              <p
                                className="absolute top-4 left-3.5 text-xs"
                                style={{ color: theme.mutedText }}
                              >
                                Selecione a quantidade de parcelas:
                              </p>
                              <select
                                value={installments || 1}
                                onChange={(e) =>
                                  setInstallments(Number(e.target.value))
                                }
                                className="pl-0.5 pt-7 pb-1 bg-transparent h-full w-full focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                style={{
                                  color: theme.inputText,
                                  backgroundColor: "transparent",
                                  WebkitAppearance: "none",
                                  MozAppearance: "none",
                                  appearance: "none",
                                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='none' stroke='%23${theme.mutedText.replace(
                                    "#",
                                    ""
                                  )}' stroke-width='1' stroke-linecap='round' stroke-linejoin='round' d='M2 3.5L5 6.5L8 3.5'/%3E%3C/svg%3E")`,
                                  backgroundRepeat: "no-repeat",
                                  backgroundPosition: "right 0.1rem center",
                                  backgroundSize: "0.825rem",
                                  paddingRight: "2.5rem",
                                }}
                                required
                              >
                                {offer
                                  ? availableInstallments.map(
                                      (installment, index) => {
                                        return (
                                          <option
                                            key={installment.qtt}
                                            value={`${installment.qtt}`}
                                            style={{
                                              backgroundColor:
                                                theme.cardBackground,
                                              color: theme.inputText,
                                            }}
                                          >
                                            {`${installment.qtt}x`} de R${" "}
                                            {formatPrice(installment.value)}
                                            {installment.period}
                                          </option>
                                        );
                                      }
                                    )
                                  : null}
                              </select>
                            </div>
                          )}

                          {upgradeLink?.upgrade && (
                            <div
                              id="upgrade-summary"
                              className="mt-3 p-4 rounded-lg"
                              style={{
                                backgroundColor: theme.cardBackground,
                                borderColor: theme.cardBorder,
                                borderWidth: "1px",
                                borderStyle: "solid",
                              }}
                            >
                              <h3
                                className="text-sm font-medium mb-3"
                                style={{ color: theme.headingText }}
                              >
                                Upgrade: {upgradeLink.upgrade.name}
                              </h3>
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span style={{ color: theme.mutedText }}>
                                    Valor original
                                  </span>
                                  <span
                                    className="line-through"
                                    style={{ color: theme.mutedText }}
                                  >
                                    R$ {formatPrice(offer.valueInCents / 100)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span style={{ color: theme.successText }}>
                                    Desconto de upgrade{" "}
                                    {`(${getDiscountPercentage()}%)`}
                                  </span>
                                  <span style={{ color: theme.successText }}>
                                    - R${" "}
                                    {formatPrice(
                                      upgradeLink.upgrade.discount / 100
                                    )}
                                  </span>
                                </div>
                                <div
                                  className="border-t pt-2 flex justify-between"
                                  style={{ borderColor: theme.cardBorder }}
                                >
                                  <span
                                    className="font-medium"
                                    style={{ color: theme.headingText }}
                                  >
                                    Total
                                  </span>
                                  <span
                                    className="font-medium"
                                    style={{ color: theme.headingText }}
                                  >
                                    R${" "}
                                    {formatPrice(
                                      upgradeLink.upgrade.finalValue / 100
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                          {offer?.trial > 0 && (
                            <div
                              className="mt-3 p-4 rounded-lg"
                              style={{
                                backgroundColor: `${theme.primaryButton}10`,
                                borderColor: theme.primaryButton,
                                borderWidth: "1px",
                                borderStyle: "solid",
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <svg
                                  className="w-4 h-4 shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  style={{
                                    color:
                                      theme.primaryButton === "#A7E600"
                                        ? "#64a30d"
                                        : theme.primaryButton,
                                  }}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                <div className="flex-1">
                                  <p
                                    className="text-sm"
                                    style={{
                                      color:
                                        theme.primaryButton === "#A7E600"
                                          ? "#64a30d"
                                          : theme.primaryButton,
                                    }}
                                  >
                                    <span className="font-medium">
                                      Período de teste:
                                    </span>{" "}
                                    Você não será cobrado hoje
                                  </p>
                                  <p
                                    className="text-xs mt-1"
                                    style={{ color: theme.mutedText }}
                                  >
                                    A cobrança de{" "}
                                    {Number(installments) > 1
                                      ? `${installments}x de R$ ${formatPrice(
                                          getSelectedInstallmentsValue()
                                        )}`
                                      : `R$ ${formatPrice(
                                          getSelectedInstallmentsValue()
                                        )}`}{" "}
                                    iniciará após {offer.trial} dias
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Container para mostrar preço quando é monthly */}
                      {offer?.period === "monthly" && (
                        <div
                          className="p-4 rounded-lg"
                          style={{
                            backgroundColor: theme.cardBackground,
                            borderColor: theme.cardBorder,
                            borderWidth: "1px",
                            borderStyle: "solid",
                          }}
                        >
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span
                                className="text-sm font-medium"
                                style={{ color: theme.mutedText }}
                              >
                                Valor:
                              </span>
                              <span
                                className="text-base sm:text-lg font-semibold"
                                style={{ color: theme.headingText }}
                              >
                                R${" "}
                                {formatPrice(
                                  upgradeLink?.upgrade
                                    ? upgradeLink.upgrade.finalValue / 100
                                    : offer.valueInCents / 100
                                )}
                              </span>
                            </div>

                            <div className="flex justify-between items-center">
                              <span
                                className="text-sm font-medium"
                                style={{ color: theme.mutedText }}
                              >
                                Recorrência:
                              </span>
                              <span
                                className="text-sm font-medium"
                                style={{ color: theme.headingText }}
                              >
                                Mensal
                              </span>
                            </div>

                            {offer?.trial > 0 && (
                              <div className="flex justify-between items-center">
                                <span
                                  className="text-sm font-medium"
                                  style={{ color: theme.mutedText }}
                                >
                                  Período de teste:
                                </span>
                                <span
                                  className="text-sm font-medium px-2 py-1 rounded-full"
                                  style={{
                                    backgroundColor: `${theme.primaryButton}15`,
                                    color:
                                      theme.primaryButton === "#A7E600"
                                        ? "#64a30d"
                                        : theme.primaryButton,
                                  }}
                                >
                                  {offer.trial} dias grátis
                                </span>
                              </div>
                            )}
                          </div>
                          {upgradeLink?.upgrade && (
                            <div
                              className="mt-3 pt-3 border-t"
                              style={{ borderColor: theme.cardBorder }}
                            >
                              <div className="flex justify-between text-sm">
                                <span style={{ color: theme.mutedText }}>
                                  Valor original
                                </span>
                                <span
                                  className="line-through"
                                  style={{ color: theme.mutedText }}
                                >
                                  R$ {formatPrice(offer.valueInCents / 100)}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm mt-1">
                                <span style={{ color: theme.successText }}>
                                  Desconto de upgrade ({getDiscountPercentage()}
                                  %)
                                </span>
                                <span style={{ color: theme.successText }}>
                                  - R${" "}
                                  {formatPrice(
                                    upgradeLink.upgrade.discount / 100
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {paymentMethod === "pix" &&
                        !(pendingPurchaseDoc?.status === "pix_generated") && (
                          <div
                            id="pix-instructions"
                            className="mt-3 p-5 rounded-lg"
                            style={{
                              backgroundColor: theme.cardBackground,
                              borderColor: theme.cardBorder,
                              borderWidth: "1px",
                              borderStyle: "solid",
                            }}
                          >
                            <h3
                              className="text-base font-semibold mb-4"
                              style={{ color: primaryColor }}
                            >
                              Pague com segurança via PIX
                            </h3>
                            <ul className="space-y-3">
                              <li className="flex items-start gap-2">
                                <span
                                  className="text-lg mt-[-2px]"
                                  style={{ color: theme.bodyText }}
                                >
                                  •
                                </span>
                                <span
                                  className="text-sm"
                                  style={{ color: theme.bodyText }}
                                >
                                  Valor à vista:{" "}
                                  <strong>
                                    R${" "}
                                    {formatPrice(getPixTotalValue())}
                                  </strong>
                                  {upgradeLink?.upgrade && (
                                    <span style={{ color: theme.successText }}>
                                      {" "}
                                      (desconto de {getDiscountPercentage()}%
                                      aplicado)
                                    </span>
                                  )}
                                  {getAcceptedOrderBumpTotal() > 0 && (
                                    <span style={{ color: theme.successText }}>
                                      {" "}
                                      (inclui ofertas adicionais)
                                    </span>
                                  )}
                                  .
                                </span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span
                                  className="text-lg mt-[-2px]"
                                  style={{ color: theme.bodyText }}
                                >
                                  •
                                </span>
                                <span
                                  className="text-sm"
                                  style={{ color: theme.bodyText }}
                                >
                                  O produto será liberado imediatamente, após a
                                  confirmação do pagamento.
                                </span>
                              </li>
                            </ul>
                          </div>
                        )}
                      {orderBumps.length > 0 &&
                        !upgradeLink?.upgrade &&
                        !(Number(offer?.trial) > 0) && (
                          <div className="mt-3 space-y-3">
                            {orderBumps.map((ob) => {
                              // `ob` is from checkout API: `offer` and `product` (ids + copy) come from
                              // the bump’s offers2 document (embedded `product` snapshot on the offer),
                              // not from a live `products` collection read in the client.
                              const obKey = `${ob.product?.id ?? ""}|${ob.offer?.code ?? ""}`;
                              const bumpName =
                                (ob.offer?.name &&
                                  String(ob.offer.name).trim()) ||
                                (ob.offer?.code &&
                                  String(ob.offer.code).trim()) ||
                                "Oferta adicional";
                              const customTitle =
                                ob.title && String(ob.title).trim();
                              const bumpTitle =
                                customTitle ||
                                `Sim, quero adicionar: ${bumpName}`;
                              const bumpDescription =
                                (ob.description &&
                                  String(ob.description).trim()) ||
                                (ob.offer?.description &&
                                  String(ob.offer.description).trim());
                              const bumpBadge =
                                ob.badgeText && String(ob.badgeText).trim();
                              const bumpLogo =
                                ob.product?.logo &&
                                String(ob.product.logo)
                                  .replace(
                                    import.meta.env.VITE_MEDIA_OLD_URL,
                                    import.meta.env.VITE_MEDIA_URL
                                  )
                                  .trim();
                              const isBumpSubscription =
                                Boolean(ob?.plan?.code) ||
                                ob?.product?.paymentModel ===
                                  "subscription";
                              return (
                                <div
                                  key={obKey}
                                  className="p-4 rounded-lg"
                                  style={{
                                    backgroundColor: theme.cardBackground,
                                    borderColor: theme.cardBorder,
                                    borderWidth: "1px",
                                    borderStyle: "solid",
                                  }}
                                >
                                  <label
                                    className="flex gap-3 items-start cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      className="mt-1 h-4 w-4 shrink-0"
                                      checked={
                                        !!orderBumpAcceptedByKey[obKey]
                                      }
                                      onChange={(e) =>
                                        handleOrderBumpChange(
                                          obKey,
                                          e.target.checked
                                        )
                                      }
                                    />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            {bumpBadge ? (
                                              <span
                                                className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full"
                                                style={{
                                                  color: theme.successText,
                                                  backgroundColor: `${theme.successText}14`,
                                                  borderColor:
                                                    theme.successText,
                                                  borderWidth: "1px",
                                                  borderStyle: "solid",
                                                }}
                                              >
                                                {bumpBadge}
                                              </span>
                                            ) : null}
                                            <p
                                              className="text-sm font-semibold leading-snug"
                                              style={{
                                                color: theme.headingText,
                                              }}
                                            >
                                              {bumpTitle}
                                            </p>
                                          </div>
                                      {bumpDescription ? (
                                        <p
                                          className="text-xs mt-2 leading-relaxed"
                                          style={{
                                            color: theme.mutedText,
                                          }}
                                        >
                                          {bumpDescription}
                                        </p>
                                      ) : null}
                                      <p
                                        className="text-xs mt-1"
                                        style={{
                                          color: theme.mutedText,
                                        }}
                                      >
                                        {isBumpSubscription
                                          ? `Assinatura separada${
                                              ob.plan?.name
                                                ? ` — ${ob.plan.name}`
                                                : ""
                                            }`
                                          : "Pagamento único adicional"}
                                      </p>
                                      <p
                                        className="text-base font-semibold mt-2"
                                        style={{
                                          color: theme.headingText,
                                        }}
                                      >
                                        + R${" "}
                                        {formatPrice(
                                          (ob.offer?.valueInCents || 0) /
                                            100
                                        )}
                                        {isBumpSubscription
                                          ? ob.offer?.period === "monthly"
                                            ? " / mês"
                                            : ""
                                          : ""}
                                      </p>
                                        </div>
                                        {bumpLogo ? (
                                          <img
                                            src={bumpLogo}
                                            alt=""
                                            className="hidden sm:block w-16 h-16 rounded-lg object-cover shrink-0"
                                            loading="lazy"
                                          />
                                        ) : null}
                                      </div>
                                    </div>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      {errors.card ? (
                        <div
                          className={`mt-2.5 p-3 text-sm sm:text-[16px] rounded-md h-16 flex items-center justify-center gap-3 w-full transition-all cursor-default`}
                          style={{
                            backgroundColor: `${theme.errorText}10`,
                            borderColor: theme.errorText,
                            borderWidth:
                              paymentMethod === "credit_card" ? "1px" : "0",
                            color: theme.errorText,
                          }}
                        >
                          <CircleSlashIcon />
                          <p className="font-normal">
                            <b>{errors.card.error_type}:</b>{" "}
                            {errors.card.error_message}.
                          </p>
                        </div>
                      ) : null}
                      {errors.recaptcha ? (
                        <div
                          className="mt-2.5 p-3 text-sm sm:text-[16px] rounded-md h-16 flex items-center justify-center gap-3 w-full transition-all cursor-default"
                          style={{
                            backgroundColor: `${theme.errorText}10`,
                            borderColor: theme.errorText,
                            borderWidth: "1px",
                            color: theme.errorText,
                          }}
                        >
                          <CircleSlashIcon />
                          <p className="font-normal">{errors.recaptcha}.</p>
                        </div>
                      ) : null}
                      {errors.customerData ? (
                        <div
                          className="mt-2.5 p-3 text-sm sm:text-[16px] rounded-md h-16 flex items-center justify-center gap-3 w-full transition-all cursor-default"
                          style={{
                            backgroundColor: `${theme.errorText}10`,
                            borderColor: theme.errorText,
                            borderWidth: "1px",
                            color: theme.errorText,
                          }}
                        >
                          <CircleSlashIcon />
                          <p className="font-normal">{errors.customerData}.</p>
                        </div>
                      ) : null}
                      {errors.pix ? (
                        <div
                          className={`mt-2.5 p-3 text-sm sm:text-[16px] rounded-md h-16 flex items-center justify-center gap-3 w-full transition-all cursor-default`}
                          style={{
                            backgroundColor: `${theme.errorText}10`,
                            borderColor: theme.errorText,
                            borderWidth: paymentMethod === "pix" ? "1px" : "0",
                            color: theme.errorText,
                          }}
                        >
                          <CircleSlashIcon />
                          <p className="font-normal">
                            <b>{errors.pix.error_type}:</b>{" "}
                            {errors.pix.error_message}.
                          </p>
                        </div>
                      ) : null}
                      {errors.purchase ? (
                        <div
                          className="mt-2.5 p-3 text-sm sm:text-[16px] rounded-md h-16 flex items-center justify-center gap-3 w-full transition-all cursor-default"
                          style={{
                            backgroundColor: `${theme.errorText}10`,
                            borderColor: theme.errorText,
                            borderWidth: "1px",
                            color: theme.errorText,
                          }}
                        >
                          <CircleSlashIcon />
                          <p className="font-normal">
                            <b>{errors.purchase.error_type}:</b>{" "}
                            {errors.purchase.error_message}.
                          </p>
                        </div>
                      ) : null}
                      <div className="mt-1 flex flex-col gap-1.5">
                        <div className="flex items-center gap-3 mt-2">
                          <LockClosedIcon
                            className="shrink-0"
                            style={{
                              color: primaryColor,
                            }}
                          />
                          <p
                            className="text-xs"
                            style={{ color: theme.mutedText }}
                          >
                            Para sua segurança, nós protegemos seus dados de
                            pagamento usando encriptação a nível de bancos.
                          </p>
                        </div>
                        {paymentMethod === "pix" &&
                        pendingPurchaseDoc?.status ===
                          "pix_generated" ? null : (
                          <Button
                            type="button"
                            size="xl"
                            className="mt-3 text-lg w-full hover:brightness-110"
                            style={{
                              backgroundColor:
                                theme.primaryGradientStart &&
                                theme.primaryGradientEnd
                                  ? undefined
                                  : theme.primaryButton,
                              backgroundImage:
                                theme.primaryGradientStart &&
                                theme.primaryGradientEnd
                                  ? `linear-gradient(135deg, ${theme.primaryGradientStart}, ${theme.primaryGradientEnd})`
                                  : undefined,
                              color: theme.primaryButtonText,
                            }}
                            disabled={isPending}
                            onClick={processPurchase}
                          >
                            {isPending && (
                              <ReloadIcon className="w-5 h-5 mr-2 animate-spin" />
                            )}
                            {getPurchaseButtonText()}
                          </Button>
                        )}
                      </div>
                    </form>
                    {paymentMethod === "pix" &&
                      pendingPurchaseDoc?.status === "pix_generated" && (
                        <div
                          className="mt-5 p-5 rounded-lg"
                          style={{
                            backgroundColor: theme.cardBackground,
                            borderColor: theme.cardBorder,
                            borderWidth: "1px",
                            borderStyle: "solid",
                          }}
                        >
                          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
                            <div className="w-full sm:w-[calc(66.66%_-_20px)]">
                              <h2
                                className="text-lg font-medium"
                                style={{ color: theme.headingText }}
                              >
                                {name.split(" ")[0]}, agora basta realizar o
                                pagamento.
                              </h2>
                              <p
                                className="mt-2.5 text-[14px] leading-6"
                                style={{ color: theme.mutedText }}
                              >
                                Copie o código copia e cola ou use a câmera para
                                escanear o QR Code.
                              </p>
                              <div
                                className={`relative gap-2.5 ${
                                  pixExpired
                                    ? "cursor-not-allowed opacity-50"
                                    : "cursor-pointer"
                                } flex items-center justify-center text-sm mt-5 h-16 p-2.5 rounded-md transition-all`}
                                style={{
                                  backgroundColor: hexToRgba(qrCodeColor, 0.1),
                                  borderColor: qrCodeColor,
                                  borderWidth: "1px",
                                  borderStyle: "solid",
                                  color:
                                    qrcode === "Código copiado!"
                                      ? theme.primaryColor
                                      : theme.bodyText,
                                }}
                                onClick={() => {
                                  if (pixExpired) return;
                                  copyToClipboard(qrcode);
                                  const qr = qrcode;
                                  setQrcode("Código copiado!");
                                  setTimeout(() => {
                                    setQrcode(qr);
                                  }, 2000);
                                }}
                              >
                                <p
                                  className={`line-clamp-2 overflow-hidden${
                                    pixExpired ? " blur-sm" : ""
                                  } ${
                                    qrcode === "Código copiado!"
                                      ? "invisible"
                                      : ""
                                  }`}
                                >
                                  {pendingPurchaseDoc?.pix.qrcode}
                                </p>

                                <p
                                  className={`font-medium absolute top-0 left-0 w-full h-full flex items-center justify-center ${
                                    qrcode === "Código copiado!"
                                      ? "opacity-100"
                                      : "opacity-0"
                                  }`}
                                >
                                  Código copiado!
                                </p>

                                <CopyIcon
                                  className={`shrink-0 h-5 w-5 ${
                                    qrcode === "Código copiado!"
                                      ? "invisible"
                                      : ""
                                  }`}
                                  style={{ color: qrCodeColor }}
                                />
                              </div>
                              <PixTimer
                                expirationTime={expirationTime}
                                qrcode={pendingPurchaseDoc?.pix.qrcode}
                                pixExpired={pixExpired}
                                setPixExpired={setPixExpired}
                                theme={theme}
                                qrCodeColor={qrCodeColor}
                              />
                            </div>
                            <div className="relative w-1/2 sm:w-1/3">
                              <img
                                src={pendingPurchaseDoc?.pix.url}
                                alt="Pix QR Code"
                                className={`rounded-lg w-full${
                                  pixExpired ? " blur-sm" : ""
                                }`}
                                style={{
                                  filter: pixExpired ? "blur(4px)" : "none",
                                }}
                              />
                              {pixExpired && (
                                <Button
                                  type="button"
                                  disabled={pending}
                                  onClick={async () => await processPurchase()}
                                  className="w-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 hover:brightness-110"
                                  style={{
                                    backgroundColor:
                                      theme.primaryGradientStart &&
                                      theme.primaryGradientEnd
                                        ? undefined
                                        : theme.primaryButton,
                                    backgroundImage:
                                      theme.primaryGradientStart &&
                                      theme.primaryGradientEnd
                                        ? `linear-gradient(135deg, ${theme.primaryGradientStart}, ${theme.primaryGradientEnd})`
                                        : undefined,
                                    color: theme.primaryButtonText,
                                  }}
                                >
                                  {pending && (
                                    <ReloadIcon className="w-5 h-5 mr-2 animate-spin" />
                                  )}
                                  {pending
                                    ? "Gerando pix..."
                                    : "Gerar novo pix"}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    <div className="mt-2.5 flex flex-col gap-5 justify-center items-center">
                      <img
                        src={atomicPayLogoUrl.replace(import.meta.env.VITE_MEDIA_OLD_URL, import.meta.env.VITE_MEDIA_URL)}
                        className="mt-2.5 w-28 mb-5"
                        alt=""
                      />
                      {!qrcode && (
                        <p
                          className="text-xs text-center"
                          style={{ color: theme.mutedText }}
                        >
                          Ao clicar em 'Pagar Agora', eu declaro que (i) estou
                          ciente que o AtomicPay está processando essa compra em
                          nome de {product.name} e que não possui
                          responsabilidade pelo conteúdo, oferta, e nem faz
                          controle prévio do infoproduto; (ii) que li e concordo
                          com os Termos de Compra, Termos de Uso, e Política de
                          Privacidade.
                        </p>
                      )}
                      <p
                        className="text-xs text-center"
                        style={{ color: theme.mutedText }}
                      >
                        Este site está protegido pelo Google reCAPTCHA.
                      </p>
                      <div className="flex gap-3 text-xs mb-6">
                        <a
                          href="https://policies.google.com/privacy"
                          style={{ color: theme.mutedText }}
                        >
                          Política de Privacidade
                        </a>
                        <a
                          href="https://policies.google.com/terms"
                          style={{ color: theme.mutedText }}
                        >
                          Termos de Serviço
                        </a>
                      </div>
                    </div>
                    </>
                  </div>
                  {/* Side components */}
                  {hasSideComponents && (
                    <div
                      id="checkout-side-components"
                      className="w-64 flex flex-col gap-8"
                    >
                      {sideComponents.map((component) => {
                        const isHeader =
                          component.type === "header" ||
                          (component.type === "container" &&
                            component.label?.toLowerCase().includes("header"));

                        return isHeader ? (
                          <div
                            key={component.id}
                            className="checkout-component-header"
                          >
                            <ComponentRenderer
                              component={component}
                              allComponents={components}
                              isMobile={isMobile}
                              theme={theme}
                            />
                          </div>
                        ) : (
                          <ComponentRenderer
                            key={component.id}
                            component={component}
                            allComponents={components}
                            isMobile={isMobile}
                            theme={theme}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
                {/* Bottom components */}
                {!embedCheckout && bottomComponents.length > 0 && (
                  <div
                    className={cn(
                      "mt-5 w-full",
                      !hasSideComponents && "md:w-2/3 lg:w-1/2 2xl:max-w-2xl"
                    )}
                  >
                    {bottomComponents.map((component) => {
                      const isHeader =
                        component.type === "header" ||
                        (component.type === "container" &&
                          component.label?.toLowerCase().includes("header"));

                      return isHeader ? (
                        <div
                          key={component.id}
                          className="checkout-component-header"
                        >
                          <ComponentRenderer
                            component={component}
                            allComponents={components}
                            isMobile={isMobile}
                            theme={theme}
                          />
                        </div>
                      ) : (
                        <ComponentRenderer
                          key={component.id}
                          component={component}
                          allComponents={components}
                          isMobile={isMobile}
                          theme={theme}
                        />
                      );
                    })}
                  </div>
                )}
                {/* Side components on mobile - rendered after everything else */}
                {!embedCheckout && isMobile && sideComponents.length > 0 && (
                  <div
                    id="checkout-side-components-mobile"
                    className={cn(
                      "mt-5 w-full flex flex-col gap-5",
                      "md:w-2/3 lg:w-1/2 2xl:max-w-2xl"
                    )}
                  >
                    {sideComponents.map((component) => {
                      const isHeader =
                        component.type === "header" ||
                        (component.type === "container" &&
                          component.label?.toLowerCase().includes("header"));

                      return isHeader ? (
                        <div
                          key={component.id}
                          className="checkout-component-header"
                        >
                          <ComponentRenderer
                            component={component}
                            allComponents={components}
                            isMobile={isMobile}
                            theme={theme}
                          />
                        </div>
                      ) : (
                        <ComponentRenderer
                          key={component.id}
                          component={component}
                          allComponents={components}
                          isMobile={isMobile}
                          theme={theme}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
